import moment from "moment";
import CheckBox from "../components/CheckBox";
import TooltipBox from "../components/Tooltip";
import { formatValueUnit } from "./utils";
import { formatCurrency } from "../support-extra/utils/utils";

export const rightStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
};

const centertStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

function applyAlignment(columns) {
  return columns.map((col) => ({
    ...col,
    ...(col.style === rightStyle && { right: true }),
    ...(col.style === centertStyle && { center: true }),
  }));
}

export const sortFunction = (v1, v2) => {
  const valueA = parseFloat(v1) || 0;
  const valueB = parseFloat(v2) || 0;
  return valueA - valueB;
};

export const deliveryNoteColumns = applyAlignment([
  {
    name: "Fecha creación",
    selector: (row) =>
      row.fechaCreacion
        ? moment(row.fechaCreacion).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortable: true,
    width: "11%",
    sortFunction: (a, b) =>
      new Date(a.fechaCreacion) - new Date(b.fechaCreacion),
  },
  {
    name: "Fecha albarán",
    selector: (row) =>
      row.fechaAlbaran ? moment(row.fechaAlbaran).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "11%",
    sortFunction: (a, b) => new Date(a.fechaAlbaran) - new Date(b.fechaAlbaran),
  },
  {
    name: "Proveedor",
    selector: (row) => row.proveedor,
    sortable: true,
    width: "18%",
  },
  {
    name: "Nº albarán",
    selector: (row) => row.numeroAlbaran,
    sortable: true,
    width: "10%",
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stockTotal),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.stockTotal, rowB.stockTotal),
  },
  {
    name: "Base",
    selector: (row) => formatValueUnit(row.pcnTotal, "€"),
    sortable: true,
    width: "9%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pcnTotal, rowB.pcnTotal),
  },
  {
    name: "IVA",
    selector: (row) => formatValueUnit(row.ivaTotal, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.ivaTotal, rowB.ivaTotal),
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.totalTotal, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.totalTotal, rowB.totalTotal),
  },
  {
    name: "Agente",
    selector: (row) => row.agente,
    sortable: true,
    width: "12%",
  },
  {
    name: "Borrador",
    selector: (row) => (row.borrador ? "Sí" : "No"),
    sortable: true,
    width: "7%",
  },
]);

export const hearingAidsColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "auto",
    cell: (row) =>
      row.idProductoAlbaran !== "summary" && !row.almacen ? (
        <div
          data-tag="allowedRowEvents"
          style={{ display: "flex", alignItems: "center", gap: "5px" }}
        >
          <img
            src={`/images/new-product-blue.svg`}
            style={{ width: "30px" }}
            alt="New product"
          ></img>
          <span>{row.codigo}</span>
        </div>
      ) : (
        <span style={{ fontWeight: 500, fontSize: 12 }}>{row.codigo}</span>
      ),
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "auto",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "auto",
  },
  {
    name: "Tipo",
    selector: (row) => row.tipo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Entre dB",
    selector: (row) => row.entreDB,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Hasta dB",
    selector: (row) => row.hastaDB,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "PVO",
    selector: (row) => (row.pvo !== "" ? formatValueUnit(row.pvo, "€") : ""),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP rec.",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.idProductoAlbaran}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.idProductoAlbaran)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const lensesColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "auto",
    cell: (row) =>
      row.idProductoAlbaran !== "summary" && !row.almacen ? (
        <div
          data-tag="allowedRowEvents"
          style={{ display: "flex", alignItems: "center", gap: "5px" }}
        >
          <img
            src={`/images/new-product-blue.svg`}
            style={{ width: "30px" }}
            alt="New product"
          ></img>
          <span>{row.codigo}</span>
        </div>
      ) : (
        <span>{row.codigo}</span>
      ),
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "10%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "12%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: true,
    width: "8%",
  },
  {
    name: "Tratamiento",
    selector: (row) => row.tratamiento,
    sortable: true,
    width: "8%",
  },
  {
    name: "Índ",
    selector: (row) => row.ind,
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Add",
    selector: (row) => row.adicion,
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Total",
    selector: (row) =>
      row.idProductoAlbaran !== "summary" ? formatValueUnit(row.total) : "",
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "PVO",
    selector: (row) => (row.pvo !== "" ? formatValueUnit(row.pvo, "€") : ""),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP rec.",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.idProductoAlbaran}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.idProductoAlbaran)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const contactLensesColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "auto",
    cell: (row) =>
      row.idProductoAlbaran !== "summary" && !row.almacen ? (
        <div
          data-tag="allowedRowEvents"
          style={{ display: "flex", alignItems: "center", gap: "5px" }}
        >
          <img
            src={`/images/new-product-blue.svg`}
            style={{ width: "30px" }}
            alt="New product"
          ></img>
          <span>{row.codigo}</span>
        </div>
      ) : (
        <span>{row.codigo}</span>
      ),
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "8%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "10%",
  },
  {
    name: "Tipo",
    selector: (row) => row.tipo,
    sortable: true,
    width: "5%",
  },
  {
    name: "Uso",
    selector: (row) => row.uso,
    sortable: true,
    width: "5%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: true,
    width: "6%",
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: true,
    width: "4%",
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: true,
    width: "4%",
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: true,
    width: "4%",
  },
  {
    name: "Add",
    selector: (row) => row.adicion,
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Total",
    selector: (row) =>
      row.idProductoAlbaran !== "summary" ? formatValueUnit(row.total) : "",
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Diá",
    selector: (row) => row.dia,
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Rad1",
    selector: (row) => row.rad,
    sortable: true,
    width: "4%",
  },
  {
    name: "Rad2",
    selector: (row) => row.rad2,
    sortable: true,
    width: "4%",
  },
  {
    name: "PVO",
    selector: (row) => (row.pvo !== "" ? formatValueUnit(row.pvo, "€") : ""),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP rec.",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.idProductoAlbaran}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.idProductoAlbaran)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const liquidColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "auto",
    cell: (row) =>
      row.idProductoAlbaran !== "summary" && !row.almacen ? (
        <div
          data-tag="allowedRowEvents"
          style={{ display: "flex", alignItems: "center", gap: "5px" }}
        >
          <img
            src={`/images/new-product-blue.svg`}
            style={{ width: "30px" }}
            alt="New product"
          ></img>
          <span>{row.codigo}</span>
        </div>
      ) : (
        <span>{row.codigo}</span>
      ),
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "18%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "auto",
  },
  {
    name: "Capacidad (ml)",
    selector: (row) => row.capacidad,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "PVO",
    selector: (row) => (row.pvo !== "" ? formatValueUnit(row.pvo, "€") : ""),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP rec.",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.idProductoAlbaran}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.idProductoAlbaran)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const frameColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "auto",
    cell: (row) =>
      row.idProductoAlbaran !== "summary" && !row.almacen ? (
        <div
          data-tag="allowedRowEvents"
          style={{ display: "flex", alignItems: "center", gap: "5px" }}
        >
          <img
            src={`/images/new-product-blue.svg`}
            style={{ width: "30px" }}
            alt="New product"
          ></img>
          <span>{row.codigo}</span>
        </div>
      ) : (
        <span style={{ fontWeight: 500, fontSize: 12 }}>{row.codigo}</span>
      ),
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "10%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: true,
    width: "8%",
  },
  {
    name: "Calibre",
    selector: (row) => row.calibre,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Puente",
    selector: (row) => row.puente,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Varilla",
    selector: (row) => row.varilla,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Material",
    selector: (row) => row.material,
    sortable: true,
    width: "10%",
  },
  {
    name: "Forma",
    selector: (row) => row.forma,
    sortable: true,
    width: "10%",
  },
  {
    name: "PVO",
    selector: (row) => (row.pvo !== "" ? formatValueUnit(row.pvo, "€") : ""),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP rec.",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.idProductoAlbaran}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.idProductoAlbaran)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const variousColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "auto",
    cell: (row) =>
      row.idProductoAlbaran !== "summary" && !row.almacen ? (
        <div
          data-tag="allowedRowEvents"
          style={{ display: "flex", alignItems: "center", gap: "5px" }}
        >
          <img
            src={`/images/new-product-blue.svg`}
            style={{ width: "30px" }}
            alt="New product"
          ></img>
          <span>{row.codigo}</span>
        </div>
      ) : (
        <span>{row.codigo}</span>
      ),
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "15%",
  },
  {
    name: "Producto",
    selector: (row) => row.producto,
    sortable: true,
    width: "20%",
  },
  {
    name: "Categoría",
    selector: (row) => row.categoria,
    sortable: true,
    width: "15%",
  },
  {
    name: "PVO",
    selector: (row) => (row.pvo !== "" ? formatValueUnit(row.pvo, "€") : ""),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP rec.",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.idProductoAlbaran}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.idProductoAlbaran)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const sunglassesColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "auto",
    cell: (row) =>
      row.idProductoAlbaran !== "summary" && !row.almacen ? (
        <div
          data-tag="allowedRowEvents"
          style={{ display: "flex", alignItems: "center", gap: "5px" }}
        >
          <img
            src={`/images/new-product-blue.svg`}
            style={{ width: "30px" }}
            alt="New product"
          ></img>
          <span>{row.codigo}</span>
        </div>
      ) : (
        <span style={{ fontWeight: 500, fontSize: 12 }}>{row.codigo}</span>
      ),
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "8%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Color montura",
    selector: (row) => row.color,
    sortable: true,
    width: "8%",
  },
  {
    name: "Color lente",
    selector: (row) => row.colorCristal,
    sortable: true,
    width: "8%",
  },
  {
    name: "Calibre",
    selector: (row) => row.calibre,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Puente",
    selector: (row) => row.puente,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Varilla",
    selector: (row) => row.varilla,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Material",
    selector: (row) => row.material,
    sortable: true,
    width: "7%",
  },
  {
    name: "Forma",
    selector: (row) => row.forma,
    sortable: true,
    width: "7%",
  },
  {
    name: "PVO",
    selector: (row) => (row.pvo !== "" ? formatValueUnit(row.pvo, "€") : ""),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP rec.",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.idProductoAlbaran}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.idProductoAlbaran)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const purchaseHearingAidsColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "15%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "8%",
    },
    {
      name: "Modelo",
      selector: (row) => row.modelo,
      sortable: true,
      width: "8%",
    },
    {
      name: "Tipo",
      selector: (row) => row.tipo,
      sortable: true,
      width: "5%",
    },
    {
      name: "Entre dB",
      selector: (row) => row.entreDB,
      sortable: true,
      width: "6%",
      style: rightStyle,
    },
    {
      name: "Hasta dB",
      selector: (row) => row.hastaDB,
      sortable: true,
      width: "6%",
      style: rightStyle,
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      width: "6%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "PCB",
      selector: (row) => row.pcb,
      sortable: false,
      width: "6%",
    },
    {
      name: "DTO %",
      selector: (row) => formatValueUnit(row.descuento),
      sortable: false,
      width: "6%",
    },
    {
      name: "PCN",
      selector: (row) => row.pcn,
      sortable: false,
      width: "6%",
    },
    {
      name: "IVA",
      selector: (row) => formatValueUnit(row.iva, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.iva, rowB.iva),
    },
    {
      name: "RE",
      selector: (row) => formatValueUnit(row.recargo, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.recargo, rowB.recargo),
    },
    {
      name: "PCU",
      selector: (row) => formatValueUnit(row.pcu, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pcu, rowB.pcu),
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: true,
      width: "4%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.stock, rowB.stock),
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.totalCompra, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.totalCompra, rowB.totalCompra),
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosCompra ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const purchaseLensesColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "14%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "8%",
    },
    {
      name: "Nombre comercial",
      selector: (row) => row.nombreComercial,
      sortable: true,
      width: "9%",
    },
    {
      name: "Color",
      selector: (row) => row.color,
      sortable: true,
      width: "8%",
    },
    {
      name: "Tratamiento",
      selector: (row) => row.tratamiento,
      sortable: true,
      width: "9%",
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      width: "6%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "PCB",
      selector: (row) => row.pcb,
      sortable: false,
      width: "6%",
    },
    {
      name: "DTO %",
      selector: (row) => formatValueUnit(row.descuento),
      sortable: false,
      width: "6%",
    },
    {
      name: "PCN",
      selector: (row) => row.pcn,
      sortable: false,
      width: "6%",
    },
    {
      name: "IVA",
      selector: (row) => formatValueUnit(row.iva, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.iva, rowB.iva),
    },
    {
      name: "RE",
      selector: (row) => formatValueUnit(row.recargo, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.recargo, rowB.recargo),
    },
    {
      name: "PCU",
      selector: (row) => formatValueUnit(row.pcu, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pcu, rowB.pcu),
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: true,
      width: "4%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.stock, rowB.stock),
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.totalCompra, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.totalCompra, rowB.totalCompra),
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosCompra ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const purchaseContactLensesColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "11%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "7%",
    },
    {
      name: "Nombre comercial",
      selector: (row) => row.nombreComercial,
      sortable: true,
      width: "9%",
    },
    {
      name: "Tipo",
      selector: (row) => row.tipo,
      sortable: true,
      width: "8%",
    },
    {
      name: "Uso",
      selector: (row) => row.uso,
      sortable: true,
      width: "7%",
    },
    {
      name: "Color",
      selector: (row) => row.color,
      sortable: true,
      width: "6%",
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      width: "6%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "PCB",
      selector: (row) => row.pcb,
      sortable: false,
      width: "6%",
    },
    {
      name: "DTO %",
      selector: (row) => formatValueUnit(row.descuento),
      sortable: false,
      width: "6%",
    },
    {
      name: "PCN",
      selector: (row) => row.pcn,
      sortable: false,
      width: "6%",
    },
    {
      name: "IVA",
      selector: (row) => formatValueUnit(row.iva, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.iva, rowB.iva),
    },
    {
      name: "RE",
      selector: (row) => formatValueUnit(row.recargo, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.recargo, rowB.recargo),
    },
    {
      name: "PCU",
      selector: (row) => formatValueUnit(row.pcu, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pcu, rowB.pcu),
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: true,
      width: "4%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.stock, rowB.stock),
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.totalCompra, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.totalCompra, rowB.totalCompra),
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosCompra ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const purchaseLiquidsColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "15%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "10%",
    },
    {
      name: "Nombre comercial",
      selector: (row) => row.nombreComercial,
      sortable: true,
      width: "15%",
    },
    {
      name: "Capacidad (ml)",
      selector: (row) => row.capacidad,
      sortable: true,
      width: "8%",
      style: rightStyle,
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      width: "6%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "PCB",
      selector: (row) => row.pcb,
      sortable: false,
      width: "6%",
    },
    {
      name: "DTO %",
      selector: (row) => formatValueUnit(row.descuento),
      sortable: false,
      width: "6%",
    },
    {
      name: "PCN",
      selector: (row) => row.pcn,
      sortable: false,
      width: "6%",
    },
    {
      name: "IVA",
      selector: (row) => formatValueUnit(row.iva, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.iva, rowB.iva),
    },
    {
      name: "RE",
      selector: (row) => formatValueUnit(row.recargo, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.recargo, rowB.recargo),
    },
    {
      name: "PCU",
      selector: (row) => formatValueUnit(row.pcu, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pcu, rowB.pcu),
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: true,
      width: "4%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.stock, rowB.stock),
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.totalCompra, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.totalCompra, rowB.totalCompra),
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosCompra ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const purchaseFrameColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "15%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "11%",
    },
    {
      name: "Modelo",
      selector: (row) => row.modelo,
      sortable: true,
      width: "12%",
    },
    {
      name: "Color",
      selector: (row) => row.color,
      sortable: true,
      width: "10%",
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      width: "6%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "PCB",
      selector: (row) => row.pcb,
      sortable: false,
      width: "6%",
    },
    {
      name: "DTO %",
      selector: (row) => formatValueUnit(row.descuento),
      sortable: false,
      width: "6%",
    },
    {
      name: "PCN",
      selector: (row) => row.pcn,
      sortable: false,
      width: "6%",
    },
    {
      name: "IVA",
      selector: (row) => formatValueUnit(row.iva, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.iva, rowB.iva),
    },
    {
      name: "RE",
      selector: (row) => formatValueUnit(row.recargo, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.recargo, rowB.recargo),
    },
    {
      name: "PCU",
      selector: (row) => formatValueUnit(row.pcu, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pcu, rowB.pcu),
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: true,
      width: "4%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.stock, rowB.stock),
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.totalCompra, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.totalCompra, rowB.totalCompra),
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosCompra ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const purchaseVariousColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "15%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "11%",
    },
    {
      name: "Producto",
      selector: (row) => row.producto,
      sortable: true,
      width: "12%",
    },
    {
      name: "Categoría",
      selector: (row) => row.categoria,
      sortable: true,
      width: "10%",
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      width: "6%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "PCB",
      selector: (row) => row.pcb,
      sortable: false,
      width: "6%",
    },
    {
      name: "DTO %",
      selector: (row) => formatValueUnit(row.descuento),
      sortable: false,
      width: "6%",
    },
    {
      name: "PCN",
      selector: (row) => row.pcn,
      sortable: false,
      width: "6%",
    },
    {
      name: "IVA",
      selector: (row) => formatValueUnit(row.iva, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.iva, rowB.iva),
    },
    {
      name: "RE",
      selector: (row) => formatValueUnit(row.recargo, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.recargo, rowB.recargo),
    },
    {
      name: "PCU",
      selector: (row) => formatValueUnit(row.pcu, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pcu, rowB.pcu),
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: true,
      width: "4%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.stock, rowB.stock),
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.totalCompra, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.totalCompra, rowB.totalCompra),
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosCompra ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const purchaseSunglassesColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "15%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "9%",
    },
    {
      name: "Modelo",
      selector: (row) => row.modelo,
      sortable: true,
      width: "8%",
    },
    {
      name: "Color montura",
      selector: (row) => row.colorMontura,
      sortable: true,
      width: "8%",
    },
    {
      name: "Color lente",
      selector: (row) => row.colorCristal,
      sortable: true,
      width: "8%",
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      width: "6%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "PCB",
      selector: (row) => row.pcb,
      sortable: false,
      width: "6%",
    },
    {
      name: "DTO %",
      selector: (row) => formatValueUnit(row.descuento),
      sortable: false,
      width: "6%",
    },
    {
      name: "PCN",
      selector: (row) => row.pcn,
      sortable: false,
      width: "6%",
    },
    {
      name: "IVA",
      selector: (row) => formatValueUnit(row.iva, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.iva, rowB.iva),
    },
    {
      name: "RE",
      selector: (row) => formatValueUnit(row.recargo, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.recargo, rowB.recargo),
    },
    {
      name: "PCU",
      selector: (row) => formatValueUnit(row.pcu, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pcu, rowB.pcu),
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: true,
      width: "4%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.stock, rowB.stock),
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.totalCompra, "€"),
      sortable: true,
      width: "5%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.totalCompra, rowB.totalCompra),
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosCompra ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const salesHearingAidsColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "15%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "15%",
    },
    {
      name: "Modelo",
      selector: (row) => row.modelo,
      sortable: true,
      width: "20%",
    },
    {
      name: "Tipo",
      selector: (row) => row.tipo,
      sortable: true,
      width: "10%",
    },
    {
      name: "Entre dB",
      selector: (row) => row.entreDB,
      sortable: true,
      width: "8%",
      style: rightStyle,
    },
    {
      name: "Hasta dB",
      selector: (row) => row.hastaDB,
      sortable: true,
      width: "8%",
      style: rightStyle,
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: false,
      width: "10%",
      style: rightStyle,
    },
    {
      name: "PVP",
      selector: (row) => row.pvp,
      sortable: false,
      width: "10%",
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosVenta ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const salesLensesColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "10%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "10%",
    },
    {
      name: "Nombre comercial",
      selector: (row) => row.nombreComercial,
      sortable: true,
      width: "11%",
    },
    {
      name: "Color",
      selector: (row) => row.color,
      sortable: true,
      width: "10%",
    },
    {
      name: "Tratamiento",
      selector: (row) => row.tratamiento,
      sortable: true,
      width: "10%",
    },
    {
      name: "Índ",
      selector: (row) => row.ind,
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Esf",
      selector: (row) =>
        row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Cil",
      selector: (row) =>
        row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Eje",
      selector: (row) => row.eje,
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Add",
      selector: (row) => row.add,
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.total),
      sortable: true,
      width: "5%",
      style: rightStyle,
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: false,
      width: "10%",
      style: rightStyle,
    },
    {
      name: "PVP",
      selector: (row) => row.pvp,
      sortable: false,
      width: "10%",
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosVenta ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);
export const salesContactLensesColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "9%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "8%",
    },
    {
      name: "Nombre comercial",
      selector: (row) => row.nombreComercial,
      sortable: true,
      width: "10%",
    },
    {
      name: "Tipo",
      selector: (row) => row.tipo,
      sortable: true,
      width: "5%",
    },
    {
      name: "Uso",
      selector: (row) => row.uso,
      sortable: true,
      width: "5%",
    },
    {
      name: "Color",
      selector: (row) => row.color,
      sortable: true,
      width: "7%",
    },
    {
      name: "Esf",
      selector: (row) =>
        row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
      sortable: true,
      width: "4%",
    },
    {
      name: "Cil",
      selector: (row) =>
        row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
      sortable: true,
      width: "4%",
    },
    {
      name: "Eje",
      selector: (row) => row.eje,
      sortable: true,
      width: "4%",
    },
    {
      name: "Add",
      selector: (row) => row.add,
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.total),
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Diá",
      selector: (row) => row.dia,
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Rad1",
      selector: (row) => row.rad,
      sortable: true,
      width: "4%",
    },
    {
      name: "Rad2",
      selector: (row) => row.rad2,
      sortable: true,
      width: "4%",
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: false,
      width: "10%",
      style: rightStyle,
    },
    {
      name: "PVP",
      selector: (row) => row.pvp,
      sortable: false,
      width: "10%",
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosVenta ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);
export const salesLiquidsColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "20%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "20%",
    },
    {
      name: "Nombre comercial",
      selector: (row) => row.nombreComercial,
      sortable: true,
      width: "26%",
    },
    {
      name: "Capacidad (ml)",
      selector: (row) => formatValueUnit(row.capacidad),
      sortable: true,
      width: "10%",
      style: rightStyle,
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: false,
      width: "10%",
      style: rightStyle,
    },
    {
      name: "PVP",
      selector: (row) => row.pvp,
      sortable: false,
      width: "10%",
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosVenta ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const salesFrameColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "10%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "10%",
    },
    {
      name: "Modelo",
      selector: (row) => row.modelo,
      sortable: true,
      width: "14%",
    },
    {
      name: "Color",
      selector: (row) => row.color,
      sortable: true,
      width: "8%",
    },
    {
      name: "Calibre",
      selector: (row) => row.calibre,
      sortable: true,
      width: "6%",
      style: rightStyle,
    },
    {
      name: "Puente",
      selector: (row) => row.puente,
      sortable: true,
      width: "6%",
      style: rightStyle,
    },
    {
      name: "Varilla",
      selector: (row) => row.varilla,
      sortable: true,
      width: "6%",
      style: rightStyle,
    },
    {
      name: "Material",
      selector: (row) => row.material,
      sortable: true,
      width: "8%",
    },
    {
      name: "Forma",
      selector: (row) => row.forma,
      sortable: true,
      width: "8%",
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: false,
      width: "10%",
      style: rightStyle,
    },
    {
      name: "PVP",
      selector: (row) => row.pvp,
      sortable: false,
      width: "10%",
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosVenta ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const salesVariousColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "20%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "20%",
    },
    {
      name: "Producto",
      selector: (row) => row.producto,
      sortable: true,
      width: "21%",
    },
    {
      name: "Categoría",
      selector: (row) => row.categoria,
      sortable: true,
      width: "15%",
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: false,
      width: "10%",
      style: rightStyle,
    },
    {
      name: "PVP",
      selector: (row) => row.pvp,
      sortable: false,
      width: "10%",
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosVenta ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);
export const salesSunglassesColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "10%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "8%",
    },
    {
      name: "Modelo",
      selector: (row) => row.modelo,
      sortable: true,
      width: "11%",
    },
    {
      name: "Color montura",
      selector: (row) => row.colorMontura,
      sortable: true,
      width: "8%",
    },
    {
      name: "Color lente",
      selector: (row) => row.colorCristal,
      sortable: true,
      width: "8%",
    },
    {
      name: "Calibre",
      selector: (row) => row.calibre,
      sortable: true,
      width: "5%",
      style: rightStyle,
    },
    {
      name: "Puente",
      selector: (row) => row.puente,
      sortable: true,
      width: "5%",
      style: rightStyle,
    },
    {
      name: "Varilla",
      selector: (row) => row.varilla,
      sortable: true,
      width: "5%",
      style: rightStyle,
    },
    {
      name: "Material",
      selector: (row) => row.material,
      sortable: true,
      width: "8%",
    },
    {
      name: "Forma",
      selector: (row) => row.forma,
      sortable: true,
      width: "8%",
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: false,
      width: "10%",
      style: rightStyle,
    },
    {
      name: "PVP",
      selector: (row) => row.pvp,
      sortable: false,
      width: "10%",
    },
    {
      name: (
        <img
          src={`/images/check-white.svg`}
          className="icon-pref"
          alt="Validado"
        ></img>
      ),
      selector: (row) =>
        row.validadoDatosVenta ? (
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Validado"
          ></img>
        ) : (
          ""
        ),
      sortable: false,
      width: "2%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "2%",
    },
  ]);

export const labelEncodingHearingAidsColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "15%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "15%",
    },
    {
      name: "Modelo",
      selector: (row) => row.modelo,
      sortable: true,
      width: "16%",
    },
    {
      name: "Tipo",
      selector: (row) => row.tipo,
      sortable: true,
      width: "10%",
    },
    {
      name: "Entre dB",
      selector: (row) => row.entreDB,
      sortable: true,
      width: "8%",
      style: rightStyle,
    },
    {
      name: "Hasta dB",
      selector: (row) => row.hastaDB,
      sortable: true,
      width: "8%",
      style: rightStyle,
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: true,
      width: "9%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.pvpRecomendado, rowB.pvpRecomendado),
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      style: rightStyle,
      width: "9%",
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "",
      id: "code",
      selector: (row) => (
        <button onClick={() => {}}>
          <img
            src={"/images/code-blue.svg"}
            className="icon-pref"
            alt="Imprimir etiqueta"
          ></img>
        </button>
      ),
      sortable: false,
      width: "3%",
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: false,
      style: rightStyle,
      width: "4%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "3%",
    },
  ]);

export const labelEncodingLensesColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "8%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "9%",
    },
    {
      name: "Nombre comercial",
      selector: (row) => row.nombreComercial,
      sortable: true,
      width: "11%",
    },
    {
      name: "Color",
      selector: (row) => row.color,
      sortable: true,
      width: "10%",
    },
    {
      name: "Tratamiento",
      selector: (row) => row.tratamiento,
      sortable: true,
      width: "10%",
    },
    {
      name: "Índ",
      selector: (row) => row.ind,
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Esf",
      selector: (row) =>
        row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Cil",
      selector: (row) =>
        row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Eje",
      selector: (row) => row.eje,
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Add",
      selector: (row) => row.add,
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.total),
      sortable: true,
      width: "5%",
      style: rightStyle,
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: true,
      width: "9%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.pvpRecomendado, rowB.pvpRecomendado),
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      style: rightStyle,
      width: "8%",
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "",
      id: "code",
      selector: (row) => (
        <button onClick={() => {}}>
          <img
            src={"/images/code-blue.svg"}
            className="icon-pref"
            alt="Imprimir etiqueta"
          ></img>
        </button>
      ),
      sortable: false,
      width: "3%",
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: false,
      style: rightStyle,
      width: "4%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "3%",
    },
  ]);
export const labelEncodingContactLensesColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "8%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "7%",
    },
    {
      name: "Nombre comercial",
      selector: (row) => row.nombreComercial,
      sortable: true,
      width: "10%",
    },
    {
      name: "Tipo",
      selector: (row) => row.tipo,
      sortable: true,
      width: "5%",
    },
    {
      name: "Uso",
      selector: (row) => row.uso,
      sortable: true,
      width: "5%",
    },
    {
      name: "Color",
      selector: (row) => row.color,
      sortable: true,
      width: "6%",
    },
    {
      name: "Esf",
      selector: (row) =>
        row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
      sortable: true,
      width: "4%",
    },
    {
      name: "Cil",
      selector: (row) =>
        row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
      sortable: true,
      width: "4%",
    },
    {
      name: "Eje",
      selector: (row) => row.eje,
      sortable: true,
      width: "4%",
    },
    {
      name: "Add",
      selector: (row) => row.add,
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Total",
      selector: (row) => formatValueUnit(row.total),
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Diá",
      selector: (row) => row.dia,
      sortable: true,
      width: "4%",
      style: rightStyle,
    },
    {
      name: "Rad1",
      selector: (row) => row.rad,
      sortable: true,
      width: "4%",
    },
    {
      name: "Rad2",
      selector: (row) => row.rad2,
      sortable: true,
      width: "4%",
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: true,
      width: "9%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.pvpRecomendado, rowB.pvpRecomendado),
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      style: rightStyle,
      width: "8%",
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "",
      id: "code",
      selector: (row) => (
        <button onClick={() => {}}>
          <img
            src={"/images/code-blue.svg"}
            className="icon-pref"
            alt="Imprimir etiqueta"
          ></img>
        </button>
      ),
      sortable: false,
      width: "3%",
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: false,
      style: rightStyle,
      width: "4%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "3%",
    },
  ]);
export const labelEncodingLiquidsColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "16%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "20%",
    },
    {
      name: "Nombre comercial",
      selector: (row) => row.nombreComercial,
      sortable: true,
      width: "26%",
    },
    {
      name: "Capacidad (ml)",
      selector: (row) => row.capacidad,
      sortable: true,
      width: "10%",
      style: rightStyle,
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: true,
      width: "9%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.pvpRecomendado, rowB.pvpRecomendado),
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      style: rightStyle,
      width: "9%",
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "",
      id: "code",
      selector: (row) => (
        <button onClick={() => {}}>
          <img
            src={"/images/code-blue.svg"}
            className="icon-pref"
            alt="Imprimir etiqueta"
          ></img>
        </button>
      ),
      sortable: false,
      width: "3%",
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: false,
      style: rightStyle,
      width: "4%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "3%",
    },
  ]);

export const labelEncodingFrameColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "9%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "10%",
    },
    {
      name: "Modelo",
      selector: (row) => row.modelo,
      sortable: true,
      width: "12%",
    },
    {
      name: "Color",
      selector: (row) => row.color,
      sortable: true,
      width: "8%",
    },
    {
      name: "Calibre",
      selector: (row) => row.calibre,
      sortable: true,
      width: "6%",
      style: rightStyle,
    },
    {
      name: "Puente",
      selector: (row) => row.puente,
      sortable: true,
      width: "6%",
      style: rightStyle,
    },
    {
      name: "Varilla",
      selector: (row) => row.varilla,
      sortable: true,
      width: "6%",
      style: rightStyle,
    },
    {
      name: "Material",
      selector: (row) => row.material,
      sortable: true,
      width: "8%",
    },
    {
      name: "Forma",
      selector: (row) => row.forma,
      sortable: true,
      width: "8%",
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: true,
      width: "9%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.pvpRecomendado, rowB.pvpRecomendado),
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      style: rightStyle,
      width: "8%",
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "",
      id: "code",
      selector: (row) => (
        <button onClick={() => {}}>
          <img
            src={"/images/code-blue.svg"}
            className="icon-pref"
            alt="Imprimir etiqueta"
          ></img>
        </button>
      ),
      sortable: false,
      width: "3%",
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: false,
      style: rightStyle,
      width: "4%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "3%",
    },
  ]);

export const labelEncodingVariousColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "16%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "20%",
    },
    {
      name: "Producto",
      selector: (row) => row.producto,
      sortable: true,
      width: "21%",
    },
    {
      name: "Categoría",
      selector: (row) => row.categoria,
      sortable: true,
      width: "15%",
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: true,
      width: "9%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.pvpRecomendado, rowB.pvpRecomendado),
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      style: rightStyle,
      width: "9%",
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "",
      id: "code",
      selector: (row) => (
        <button onClick={() => {}}>
          <img
            src={"/images/code-blue.svg"}
            className="icon-pref"
            alt="Imprimir etiqueta"
          ></img>
        </button>
      ),
      sortable: false,
      width: "3%",
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: false,
      style: rightStyle,
      width: "4%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "3%",
    },
  ]);
export const labelEncodingSunglassesColumns = () =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigo,
      sortable: true,
      width: "8%",
    },
    {
      name: "Marca",
      selector: (row) => row.marca,
      sortable: true,
      width: "8%",
    },
    {
      name: "Modelo",
      selector: (row) => row.modelo,
      sortable: true,
      width: "10%",
    },
    {
      name: "Color montura",
      selector: (row) => row.colorMontura,
      sortable: true,
      width: "8%",
    },
    {
      name: "Color lente",
      selector: (row) => row.colorCristal,
      sortable: true,
      width: "8%",
    },
    {
      name: "Calibre",
      selector: (row) => row.calibre,
      sortable: true,
      width: "5%",
      style: rightStyle,
    },
    {
      name: "Puente",
      selector: (row) => row.puente,
      sortable: true,
      width: "5%",
      style: rightStyle,
    },
    {
      name: "Varilla",
      selector: (row) => row.varilla,
      sortable: true,
      width: "5%",
      style: rightStyle,
    },
    {
      name: "Material",
      selector: (row) => row.material,
      sortable: true,
      width: "8%",
    },
    {
      name: "Forma",
      selector: (row) => row.forma,
      sortable: true,
      width: "8%",
    },
    {
      name: "PVP rec.",
      selector: (row) => formatValueUnit(row.pvpRecomendado, "€"),
      sortable: true,
      width: "9%",
      style: rightStyle,
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.pvpRecomendado, rowB.pvpRecomendado),
    },
    {
      name: "PVP",
      selector: (row) => formatValueUnit(row.pvp, "€"),
      sortable: true,
      width: "8%",
      style: rightStyle,
      sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    },
    {
      name: "",
      id: "code",
      selector: (row) => (
        <button
          onClick={() => {
            // console.log("PRODUCTO: ", row);
          }}
        >
          <img
            src={"/images/code-blue.svg"}
            className="icon-pref"
            alt="Imprimir etiqueta"
          ></img>
        </button>
      ),
      sortable: false,
      width: "3%",
    },
    {
      name: "Stock",
      selector: (row) => formatValueUnit(row.stock),
      sortable: false,
      style: rightStyle,
      width: "4%",
    },
    {
      name: "",
      selector: (row) => {},
      sortable: false,
      width: "3%",
    },
  ]);

export const purchaseSummaryColumns = applyAlignment([
  {
    name: "PCN total",
    selector: (row) => formatValueUnit(row.pcnTotal, "€"),
    sortable: false,
    width: "86%",
    style: rightStyle,
  },
  {
    name: "IVA total",
    selector: (row) => formatValueUnit(row.ivaTotal, "€"),
    sortable: false,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Importe total",
    selector: (row) => formatValueUnit(row.importeTotal, "€"),
    sortable: false,
    width: "7%",
    style: rightStyle,
  },
]);

export const hearingAidsDistributionColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: false,
    width: "20%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: false,
    width: "20%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: false,
    width: "20%",
  },
  {
    name: "Tipo",
    selector: (row) => row.tipo,
    sortable: false,
    width: "9%",
  },
  {
    name: "Entre dB",
    selector: (row) => row.entreDB,
    sortable: false,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "Hasta dB",
    selector: (row) => row.hastaDB,
    sortable: false,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: false,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: false,
    style: rightStyle,
    width: "5%",
  },
]);

export const lensesDistributionColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: false,
    width: "15%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: false,
    width: "12%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: false,
    width: "15%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: false,
    width: "12%",
  },
  {
    name: "Tratamiento",
    selector: (row) => row.tratamiento,
    sortable: false,
    width: "10%",
  },
  {
    name: "Índ",
    selector: (row) => row.ind,
    sortable: false,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: false,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: false,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: false,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Add",
    selector: (row) => row.add,
    sortable: false,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.total),
    sortable: false,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: false,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: false,
    style: rightStyle,
    width: "5%",
  },
]);

export const contactLensesDistributionColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: false,
    width: "10%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: false,
    width: "10%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: false,
    width: "10%",
  },
  {
    name: "Tipo",
    selector: (row) => row.tipo,
    sortable: false,
    width: "12%",
  },
  {
    name: "Uso",
    selector: (row) => row.uso,
    sortable: false,
    width: "7%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: false,
    width: "7%",
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: false,
    width: "4%",
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: false,
    width: "4%",
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: false,
    width: "4%",
  },
  {
    name: "Add",
    selector: (row) => row.add,
    sortable: false,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.total),
    sortable: false,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Diá",
    selector: (row) => row.dia,
    sortable: false,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Rad1",
    selector: (row) => row.rad,
    sortable: false,
    width: "4%",
  },
  {
    name: "Rad2",
    selector: (row) => row.rad2,
    sortable: false,
    width: "4%",
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: false,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: false,
    style: rightStyle,
    width: "5%",
  },
]);

export const liquidDistributionColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: false,
    width: "22%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: false,
    width: "22%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: false,
    width: "25%",
  },
  {
    name: "Capacidad (ml)",
    selector: (row) => row.capacidad,
    sortable: false,
    width: "20%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: false,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: false,
    style: rightStyle,
    width: "5%",
  },
]);

export const frameDistributionColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: false,
    width: "14%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: false,
    width: "10%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: false,
    width: "17%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: false,
    width: "8%",
  },
  {
    name: "Calibre",
    selector: (row) => row.calibre,
    sortable: false,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Puente",
    selector: (row) => row.puente,
    sortable: false,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Varilla",
    selector: (row) => row.varilla,
    sortable: false,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Material",
    selector: (row) => row.material,
    sortable: false,
    width: "8%",
  },
  {
    name: "Forma",
    selector: (row) => row.forma,
    sortable: false,
    width: "8%",
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: false,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: false,
    style: rightStyle,
    width: "5%",
  },
]);

export const variousDistributionColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: false,
    width: "22%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: false,
    width: "22%",
  },
  {
    name: "Producto",
    selector: (row) => row.producto,
    sortable: false,
    width: "25%",
  },
  {
    name: "Categoría",
    selector: (row) => row.categoria,
    sortable: false,
    width: "20%",
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: false,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: false,
    style: rightStyle,
    width: "5%",
  },
]);

export const sunglassesDistributionColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: false,
    width: "10%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: false,
    width: "9%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: false,
    width: "10%",
  },
  {
    name: "Color montura",
    selector: (row) => row.colorMontura,
    sortable: false,
    width: "10%",
  },
  {
    name: "Color lente",
    selector: (row) => row.colorCristal,
    sortable: false,
    width: "10%",
  },
  {
    name: "Calibre",
    selector: (row) => row.calibre,
    sortable: false,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Puente",
    selector: (row) => row.puente,
    sortable: false,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Varilla",
    selector: (row) => row.varilla,
    sortable: false,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Material",
    selector: (row) => row.material,
    sortable: false,
    width: "8%",
  },
  {
    name: "Forma",
    selector: (row) => row.forma,
    sortable: false,
    width: "8%",
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: false,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: false,
    style: rightStyle,
    width: "5%",
  },
]);

export const hearingAidsColumnsStore = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "19%",
  },
  {
    name: "Últ. compra",
    selector: (row) =>
      row.fechaUltimaCompra !== ""
        ? moment(row.fechaUltimaCompra).format("DD/MM/YYYY")
        : "",
    sortable: true,
    width: "10%",
    sortFunction: (a, b) =>
      (a.fechaUltimaCompra ? new Date(a.fechaUltimaCompra) : new Date(0)) -
      (b.fechaUltimaCompra ? new Date(b.fechaUltimaCompra) : new Date(0)),
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "25%",
  },
  {
    name: "Tipo",
    selector: (row) => row.tipo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Entre dB",
    selector: (row) => row.entreDB,
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Hasta dB",
    selector: (row) => row.hastaDB,
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    id: "code",
    selector: (row) => (
      <button onClick={() => {}}>
        <img
          src={"/images/code-blue.svg"}
          className="icon-pref"
          alt="Imprimir etiqueta"
        ></img>
      </button>
    ),
    style: centertStyle,
    sortable: false,
    width: "3%",
  },
  {
    name: "",
    id: "check",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "3%",
  },
]);

export const lensesFamilyStore = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Últ. compra",
    selector: (row) =>
      row.fechaUltimaCompra !== ""
        ? moment(row.fechaUltimaCompra).format("DD/MM/YYYY")
        : "",
    sortable: true,
    sortFunction: (a, b) =>
      (a.fechaUltimaCompra ? new Date(a.fechaUltimaCompra) : new Date(0)) -
      (b.fechaUltimaCompra ? new Date(b.fechaUltimaCompra) : new Date(0)),
    width: "8%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "13%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: true,
    width: "8%",
  },
  {
    name: "Tratamiento",
    selector: (row) => row.tratamiento,
    sortable: true,
    width: "10%",
  },
  {
    name: "Índ",
    selector: (row) => row.ind,
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Material",
    selector: (row) => row.material,
    sortable: true,
    width: "9%",
  },
  {
    name: "Tipo",
    selector: (row) => row.tipo,
    sortable: true,
    width: "8%",
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    id: "code",
    selector: (row) => (
      <button onClick={() => {}}>
        <img
          src={"/images/code-blue.svg"}
          className="icon-pref"
          alt="Imprimir etiqueta"
        ></img>
      </button>
    ),
    style: centertStyle,
    sortable: false,
    width: "3%",
  },
  {
    name: "",
    id: "check",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "3%",
  },
]);

export const contactLensesColumnsStore = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Últ. compra",
    selector: (row) =>
      row.fechaUltimaCompra !== ""
        ? moment(row.fechaUltimaCompra).format("DD/MM/YYYY")
        : "",
    sortable: true,
    sortFunction: (a, b) =>
      (a.fechaUltimaCompra ? new Date(a.fechaUltimaCompra) : new Date(0)) -
      (b.fechaUltimaCompra ? new Date(b.fechaUltimaCompra) : new Date(0)),
    width: "8%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "13%",
  },
  {
    name: "Tipo",
    selector: (row) => row.tipo,
    sortable: true,
    width: "6%",
  },
  {
    name: "Uso",
    selector: (row) => row.uso,
    sortable: true,
    width: "7%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: true,
    width: "8%",
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: true,
    width: "5%",
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: true,
    width: "5%",
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: true,
    width: "5%",
  },
  {
    name: "Rad1",
    selector: (row) => row.rad,
    sortable: true,
    width: "5%",
  },
  {
    name: "Rad2",
    selector: (row) => row.rad2,
    sortable: true,
    width: "5%",
  },
  {
    name: "Diá",
    selector: (row) => row.diam,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    id: "code",
    selector: (row) => (
      <button onClick={() => {}}>
        <img
          src={"/images/code-blue.svg"}
          className="icon-pref"
          alt="Imprimir etiqueta"
        ></img>
      </button>
    ),
    style: centertStyle,
    sortable: false,
    width: "3%",
  },
  {
    name: "",
    id: "check",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "3%",
  },
]);

export const liquidColumnsStore = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "18%",
  },
  {
    name: "Últ. compra",
    selector: (row) =>
      row.fechaUltimaCompra !== ""
        ? moment(row.fechaUltimaCompra).format("DD/MM/YYYY")
        : "",
    sortFunction: (a, b) =>
      (a.fechaUltimaCompra ? new Date(a.fechaUltimaCompra) : new Date(0)) -
      (b.fechaUltimaCompra ? new Date(b.fechaUltimaCompra) : new Date(0)),
    sortable: true,
    width: "10%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "15%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "27%",
  },
  {
    name: "Capacidad (ml)",
    selector: (row) => row.capacidad,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    id: "code",
    selector: (row) => (
      <button onClick={() => {}}>
        <img
          src={"/images/code-blue.svg"}
          className="icon-pref"
          alt="Imprimir etiqueta"
        ></img>
      </button>
    ),
    style: centertStyle,
    sortable: false,
    width: "3%",
  },
  {
    name: "",
    id: "check",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "3%",
  },
]);

export const frameColumnsStore = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Últ. compra",
    selector: (row) =>
      row.fechaUltimaCompra !== ""
        ? moment(row.fechaUltimaCompra).format("DD/MM/YYYY")
        : "",
    sortFunction: (a, b) =>
      (a.fechaUltimaCompra ? new Date(a.fechaUltimaCompra) : new Date(0)) -
      (b.fechaUltimaCompra ? new Date(b.fechaUltimaCompra) : new Date(0)),
    sortable: true,
    width: "9%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "9%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: true,
    width: "7%",
  },
  {
    name: "Calibre",
    selector: (row) => row.calibre,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Puente",
    selector: (row) => row.puente,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Varilla",
    selector: (row) => row.varilla,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Material",
    selector: (row) => row.material,
    sortable: true,
    width: "9%",
  },
  {
    name: "Forma",
    selector: (row) => row.forma,
    sortable: true,
    width: "8%",
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    id: "code",
    selector: (row) => (
      <button onClick={() => {}}>
        <img
          src={"/images/code-blue.svg"}
          className="icon-pref"
          alt="Imprimir etiqueta"
        ></img>
      </button>
    ),
    style: centertStyle,
    sortable: false,
    width: "3%",
  },
  {
    name: "",
    id: "check",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "3%",
  },
]);

export const variousColumnsStore = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "18%",
  },
  {
    name: "Últ. compra",
    selector: (row) =>
      row.fechaUltimaCompra !== ""
        ? moment(row.fechaUltimaCompra).format("DD/MM/YYYY")
        : "",
    sortFunction: (a, b) =>
      (a.fechaUltimaCompra ? new Date(a.fechaUltimaCompra) : new Date(0)) -
      (b.fechaUltimaCompra ? new Date(b.fechaUltimaCompra) : new Date(0)),
    sortable: true,
    width: "10%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "15%",
  },
  {
    name: "Producto",
    selector: (row) => row.producto,
    sortable: true,
    width: "22%",
  },
  {
    name: "Categoría",
    selector: (row) => row.categoria,
    sortable: true,
    width: "15%",
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    id: "code",
    selector: (row) => (
      <button onClick={() => {}}>
        <img
          src={"/images/code-blue.svg"}
          className="icon-pref"
          alt="Imprimir etiqueta"
        ></img>
      </button>
    ),
    style: centertStyle,
    sortable: false,
    width: "3%",
  },
  {
    name: "",
    id: "check",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "3%",
  },
]);

export const sunglassesColumnsStore = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Últ. compra",
    selector: (row) =>
      row.fechaUltimaCompra !== ""
        ? moment(row.fechaUltimaCompra).format("DD/MM/YYYY")
        : "",
    sortFunction: (a, b) =>
      (a.fechaUltimaCompra ? new Date(a.fechaUltimaCompra) : new Date(0)) -
      (b.fechaUltimaCompra ? new Date(b.fechaUltimaCompra) : new Date(0)),
    sortable: true,
    width: "8%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "7%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "7%",
  },
  {
    name: "Color montura",
    selector: (row) => row.colorMontura,
    sortable: true,
    width: "8%",
  },
  {
    name: "Color lente",
    selector: (row) => row.colorCristal,
    sortable: true,
    width: "8%",
  },
  {
    name: "Calibre",
    selector: (row) => row.calibre,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Puente",
    selector: (row) => row.puente,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Varilla",
    selector: (row) => row.varilla,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "Material",
    selector: (row) => row.material,
    sortable: true,
    width: "7%",
  },
  {
    name: "Forma",
    selector: (row) => row.forma,
    sortable: true,
    width: "9%",
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    id: "code",
    selector: (row) => (
      <button onClick={() => {}}>
        <img
          src={"/images/code-blue.svg"}
          className="icon-pref"
          alt="Imprimir etiqueta"
        ></img>
      </button>
    ),
    style: centertStyle,
    sortable: false,
    width: "3%",
  },
  {
    name: "",
    id: "check",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "3%",
  },
]);

export const opticColumns = applyAlignment([
  {
    name: "Nombre",
    selector: (row) => row.text,
    sortable: true,
    width: "90%",
  },
  {
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "auto",
  },
]);

export const validationColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "8%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo || row.nombreComercial || row.producto,
    sortable: true,
    width: "10%",
  },
  {
    name: "PCB",
    selector: (row) => formatValueUnit(row.pcb, "€"),
    sortable: true,
    width: "5%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pcb, rowB.pcb),
  },
  {
    name: "PCN",
    selector: (row) => formatValueUnit(row.pcn, "€"),
    sortable: true,
    width: "5%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pcn, rowB.pcn),
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: true,
    width: "5%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
  },
  {
    name: "Stock",
    selector: (row) => formatValueUnit(row.stock),
    sortable: true,
    width: "4%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.stock, rowB.stock),
  },
  {
    name: "PCB T",
    selector: (row) =>
      formatValueUnit(parseFloat(row.pcb * row.stock).toFixed(2), "€"),
    sortable: true,
    width: "auto",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        parseFloat(rowA.pcb * rowA.stock).toFixed(2),
        parseFloat(rowB.pcb * rowB.stock).toFixed(2),
      ),
  },
  {
    name: "PCN T",
    selector: (row) =>
      formatValueUnit(parseFloat(row.pcn * row.stock).toFixed(2), "€"),
    sortable: true,
    width: "auto",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        parseFloat(rowA.pcn * rowA.stock).toFixed(2),
        parseFloat(rowB.pcn * rowB.stock).toFixed(2),
      ),
  },
  {
    name: "PVP T",
    selector: (row) =>
      formatValueUnit(parseFloat(row.pvp * row.stock).toFixed(2), "€"),
    sortable: true,
    width: "auto",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        parseFloat(rowA.pvp * rowA.stock).toFixed(2),
        parseFloat(rowB.pvp * rowB.stock).toFixed(2),
      ),
  },
]);

export const validationSummaryColumns = applyAlignment([
  {
    name: "",
    selector: () => {},
    sortable: false,
    width: "41%",
  },
  {
    name: "Stock total",
    selector: (row) => formatValueUnit(row.stockTotal),
    sortable: false,
    style: rightStyle,
    width: "4%",
  },
  {
    name: "PCB total",
    selector: (row) => formatValueUnit(row.pcbTotal, "€"),
    sortable: false,
    width: "auto",
    style: rightStyle,
  },
  {
    name: "PCN total",
    selector: (row) => formatValueUnit(row.pcnTotal, "€"),
    sortable: false,
    width: "auto",
    style: rightStyle,
  },
  {
    name: "PVP total",
    selector: (row) => formatValueUnit(row.pvpTotal, "€"),
    sortable: false,
    width: "auto",
    style: rightStyle,
  },
]);

export const validationFinalSummaryColumns = applyAlignment([
  {
    name: "PCB total",
    selector: (row) => formatValueUnit(row.pcbTotal, "€"),
    sortable: false,
    width: "84%",
    style: rightStyle,
  },
  {
    name: "PCN total",
    selector: (row) => formatValueUnit(row.pcnTotal, "€"),
    sortable: false,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Stock total",
    selector: (row) => formatValueUnit(row.stockTotal),
    sortable: false,
    style: rightStyle,
    width: "8%",
  },
]);

export const extraColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Descripción",
    selector: (row) => row.descripcion,
    sortable: true,
    width: "45%",
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: true,
    width: "10%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.pvp, rowB.pvp),
    style: rightStyle,
  },
  {
    name: "IVA %",
    selector: (row) => formatValueUnit(row.iva),
    sortable: true,
    width: "10%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.iva, rowB.iva),
  },
  {
    name: "Dto %",
    // El dto se guarda como float, pero se muestra redondeado al entero.
    selector: (row) => formatValueUnit(Math.round(parseFloat(row.dto) || 0)),
    sortable: true,
    width: "10%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.dto, rowB.dto),
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.total, "€"),
    sortable: true,
    width: "10%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.total, rowB.total),
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "5%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.id}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.id)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const healthDataColumns = applyAlignment([
  {
    name: "Fecha",
    selector: (row) =>
      row.fechaCompra ? moment(row.fechaCompra).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "10%",
    sortFunction: (a, b) => new Date(a.fechaCompra) - new Date(b.fechaCompra),
  },
  {
    name: "Artículo",
    selector: (row) => row.codigoArticulo,
    sortable: true,
    width: "11%",
  },
  {
    name: "Descripción",
    selector: (row) => row.descripcion,
    sortable: true,
    width: "40%",
  },
  {
    name: "Albarán",
    selector: (row) => row.nAlbaran,
    sortable: true,
    width: "12%",
  },
  {
    name: "Expedición",
    selector: (row) => row.nExpedicion,
    sortable: true,
    width: "12%",
  },
  {
    name: "Lote",
    selector: (row) => row.nLote,
    sortable: true,
    width: "12%",
  },
  {
    name: "",
    sortable: false,
    width: "3%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.idDatoSanitario}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.idDatoSanitario)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const contactLensesTestListColumns = applyAlignment([
  {
    name: "Ojo",
    selector: (row) => row.ojo,
    sortable: false,
    width: "5%",
  },
  {
    name: "Ficha",
    selector: (row) => row.ficha,
    sortable: false,
    width: "6%",
  },
  {
    name: "Fecha",
    selector: (row) =>
      row.fecha ? moment(row.fecha).format("DD/MM/YYYY HH:mm:ss") : "",
    sortable: true,
    width: "10%",
    sortFunction: (a, b) => new Date(a.fecha) - new Date(b.fecha),
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: false,
    width: "11%",
  },
  {
    name: "Rad",
    selector: (row) => row.rad,
    sortable: false,
    width: "5%",
  },
  {
    name: "Rad2",
    selector: (row) => row.rad2,
    sortable: false,
    width: "5%",
  },
  {
    name: "Diá",
    selector: (row) => row.dia,
    sortable: false,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: false,
    width: "5%",
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: false,
    width: "5%",
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: false,
    width: "5%",
  },
  {
    name: "Add",
    selector: (row) => row.add,
    sortable: false,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Observaciones LC",
    selector: (row) => row.observacionesLC,
    sortable: false,
    width: "14%",
  },
  {
    name: "Observaciones pruebas",
    selector: (row) => row.observacionesPruebas,
    sortable: false,
    width: "19%",
  },
]);

export const salesSummaryTable = applyAlignment([
  {
    name: "ID",
    selector: (row) => row.idOptica,
    sortable: true,
    width: "10%",
  },
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "40%",
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.total, "€"),
    sortable: true,
    width: "20%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.total, rowB.total),
  },
  {
    id: "verMas",
    name: "",
    cell: (row) =>
      row.idOptica ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          Ver más
        </button>
      ) : (
        ""
      ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "15%",
  },
  {
    id: "listarVentas",
    name: "",
    cell: (row) =>
      row.idOptica ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          Listar ventas
        </button>
      ) : (
        ""
      ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "15%",
  },
]);

export const storeSupplierSummaryTable = applyAlignment([
  {
    name: "ID",
    selector: (row) => row.idProveedor,
    sortable: true,
    width: "10%",
  },
  {
    name: "Proveedor",
    selector: (row) => row.proveedor,
    sortable: true,
    width: "35%",
  },
  {
    name: "Vol. Compras",
    selector: (row) => formatValueUnit(row.volumenCompras, "€"),
    sortable: true,
    width: "20%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.volumenCompras, rowB.volumenCompras),
  },
  {
    name: "Vol. Ventas",
    selector: (row) => formatValueUnit(row.volumenVentas, "€"),
    sortable: true,
    width: "20%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.volumenVentas, rowB.volumenVentas),
  },
  {
    name: "",
    cell: (row) =>
      row.idProveedor ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          Ver más
        </button>
      ) : (
        ""
      ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "15%",
  },
]);

export const storeBrandSummaryTable = applyAlignment([
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "45%",
  },
  {
    name: "Vol. Compras",
    selector: (row) => formatValueUnit(row.volumenCompras, "€"),
    sortable: true,
    width: "20%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.volumenCompras, rowB.volumenCompras),
  },
  {
    name: "Vol. Ventas",
    selector: (row) => formatValueUnit(row.volumenVentas, "€"),
    sortable: true,
    width: "20%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.volumenVentas, rowB.volumenVentas),
  },
  {
    name: "",
    cell: (row) =>
      row.marca ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          Ver más
        </button>
      ) : (
        ""
      ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "15%",
  },
]);

export const dashboardSalesTable = applyAlignment([
  {
    name: "Nº factura",
    selector: (row) => row.nFactura,
    sortable: true,
    width: "10%",
  },
  {
    name: "Fecha",
    selector: (row) =>
      row.fecha ? moment(row.fecha).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "10%",
    sortFunction: (a, b) =>
      (a.fecha ? new Date(a.fecha) : new Date(0)) -
      (b.fecha ? new Date(b.fecha) : new Date(0)),
  },
  {
    name: "Agente",
    selector: (row) => row.agente,
    sortable: true,
    width: "20%",
  },
  {
    name: "Código",
    selector: (row) => row.codigoCliente,
    sortable: true,
    width: "6%",
  },
  {
    name: "NIF",
    selector: (row) => row.nifCliente,
    sortable: true,
    width: "10%",
  },
  {
    name: "Paciente",
    selector: (row) => row.nombreApellidosCliente,
    sortable: true,
    width: "14%",
  },
  {
    name: "Base",
    selector: (row) => formatValueUnit(row.base, "€"),
    sortable: true,
    width: "10%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.base, rowB.base),
  },
  {
    name: "IVA",
    selector: (row) => formatValueUnit(row.iva, "€"),
    sortable: true,
    width: "10%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.iva, rowB.iva),
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.total, "€"),
    sortable: true,
    width: "10%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.total, rowB.total),
  },
]);

export const dashboardSalesSummaryTable = applyAlignment([
  {
    name: "",
    selector: () => {},
    sortable: false,
    width: "auto",
  },
  {
    name: "Operaciones",
    selector: (row) => row.operaciones,
    sortable: false,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "Base",
    selector: (row) => formatValueUnit(row.base, "€"),
    sortable: false,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "IVA",
    selector: (row) => formatValueUnit(row.iva, "€"),
    sortable: false,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.total, "€"),
    sortable: false,
    width: "10%",
    style: rightStyle,
  },
]);

export const salesStatsOpticsTable = applyAlignment([
  {
    name: "ID",
    selector: (row) => row.idOptica,
    sortable: true,
    width: "10%",
  },
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "28%",
  },
  {
    name: "Total V-D",
    selector: (row) => formatValueUnit(row.totalVD, "€"),
    sortable: true,
    width: "22%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.totalVD, rowB.totalVD),
  },
  {
    name: "Beneficio %",
    selector: (row) => formatValueUnit(row.beneficio),
    sortable: true,
    width: "25%",
    style: rightStyle,
  },
  {
    name: "",
    cell: (row) =>
      row.idOptica ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          Ver más
        </button>
      ) : (
        ""
      ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "15%",
  },
]);

export const salesStatsAgentsTable = applyAlignment([
  {
    name: "ID",
    selector: (row) => row.idAgente,
    sortable: true,
    width: "10%",
  },
  {
    name: "Agente",
    selector: (row) => row.nombreApellidosAgente,
    sortable: true,
    width: "27%",
  },
  {
    name: "Total V-D",
    selector: (row) => formatValueUnit(row.totalVD, "€"),
    sortable: true,
    width: "22%",
    style: rightStyle,
  },
  {
    name: "Beneficio %",
    selector: (row) => formatValueUnit(row.beneficio),
    sortable: true,
    width: "26%",
    style: rightStyle,
  },
  {
    name: "",
    selector: (row) => (row.idAgente ? "Ver más" : ""),
    sortable: false,
    width: "15%",
  },
]);

const orangeCellBg = [
  {
    when: (row) => true,
    style: {
      backgroundColor: "#ffe7cb",
    },
  },
];

export const salesSeeMoreColumns = applyAlignment([
  {
    name: "Familia",
    selector: (row) => row.familia.toUpperCase(),
    sortable: true,
    width: "160px",
  },
  {
    name: "Uds. Vendidas",
    selector: (row) => row.udsVendidas,
    sortable: true,
    width: "130px",
  },
  {
    name: "Total ventas",
    selector: (row) => row.totalVentas,
    sortable: true,
    width: "120px",
  },
  {
    name: "Uds. Devueltas",
    selector: (row) => row.udsDevueltas,
    sortable: true,
    width: "140px",
  },
  {
    name: "Total devoluciones",
    selector: (row) => row.totalDevoluciones,
    sortable: true,
    width: "160px",
  },
  {
    name: "Total (V-D)",
    selector: (row) => formatValueUnit(row.totalVD, "€"),
    sortable: true,
    width: "120px",
    right: true,
    sortFunction: (rowA, rowB) => sortFunction(rowA.totalVD, rowB.totalVD),
    conditionalCellStyles: orangeCellBg,
  },
  {
    name: "PMV",
    selector: (row) => formatValueUnit(row.pmv, "€"),
    sortable: true,
    width: "140px",
    right: true,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pmv, rowB.pmv),
    conditionalCellStyles: orangeCellBg,
  },
  {
    name: "MCC %",
    selector: (row) => row.mcc,
    sortable: true,
    width: "80px",
    conditionalCellStyles: orangeCellBg,
  },
  {
    name: "Beneficio",
    selector: (row) => formatValueUnit(row.beneficio, "€"),
    sortable: true,
    width: "auto",
    right: true,
    sortFunction: (rowA, rowB) => sortFunction(rowA.beneficio, rowB.beneficio),
    conditionalCellStyles: orangeCellBg,
  },
  {
    name: "MCV %",
    selector: (row) => row.mcv,
    sortable: true,
    width: "80px",
    conditionalCellStyles: orangeCellBg,
  },
]);

export const storeDashboardFamilyTable = applyAlignment([
  {
    name: "Familia",
    selector: (row) => row.familia.toUpperCase(),
    sortable: true,
    width: "14%",
  },
  {
    name: "Vol. de Ventas",
    selector: (row) => formatValueUnit(row.volVentas, "€"),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.volVentas, rowB.volVentas),
  },
  {
    name: "DTO %",
    selector: (row) => formatValueUnit(row.dto),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.dto, rowB.dto),
  },
  {
    name: "Ventas netas",
    selector: (row) => formatValueUnit(row.ventasNetas, "€"),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.ventasNetas, rowB.ventasNetas),
  },
  {
    name: "Compras netas",
    selector: (row) => formatValueUnit(row.comprasNetas, "€"),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.comprasNetas, rowB.comprasNetas),
  },
  {
    name: "Uds. Compradas",
    selector: (row) => row.udsCompradas,
    sortable: true,
    right: true,
    width: "12%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.udsCompradas, rowB.udsCompradas),
  },
  {
    name: "Uds. Vendidas",
    selector: (row) => row.udsVendidas,
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.udsVendidas, rowB.udsVendidas),
  },
  {
    name: "MCC",
    selector: (row) => formatValueUnit(row.mcc, "%"),
    sortable: true,
    width: "7%",
    right: true,
    sortFunction: (rowA, rowB) => sortFunction(rowA.mcc, rowB.mcc),
  },
  {
    name: "Margen",
    selector: (row) => formatValueUnit(row.margen, "€"),
    sortable: true,
    width: "10%",
    right: true,
    sortFunction: (rowA, rowB) => sortFunction(rowA.margen, rowB.margen),
  },
  {
    name: "MCV",
    selector: (row) => `${row.mcv} %`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.mcv, rowB.mcv),
  },
]);

export const storeDashboardSupplierTable = applyAlignment([
  {
    name: "Proveedor",
    selector: (row) => row.proveedor.toUpperCase(),
    sortable: true,
    width: "14%",
  },
  {
    name: "Vol. de Ventas",
    selector: (row) => formatValueUnit(row.volVentas, "€"),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.volVentas, rowB.volVentas),
  },
  {
    name: "DTO %",
    selector: (row) => formatValueUnit(row.dto),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.dto, rowB.dto),
  },
  {
    name: "Ventas netas",
    selector: (row) => formatValueUnit(row.ventasNetas, "€"),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.ventasNetas, rowB.ventasNetas),
  },
  {
    name: "Compras netas",
    selector: (row) => formatValueUnit(row.comprasNetas, "€"),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.comprasNetas, rowB.comprasNetas),
  },
  {
    name: "Uds. Compradas",
    selector: (row) => row.udsCompradas,
    sortable: true,
    right: true,
    width: "12%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.udsCompradas, rowB.udsCompradas),
  },
  {
    name: "Uds. Vendidas",
    selector: (row) => row.udsVendidas,
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.udsVendidas, rowB.udsVendidas),
  },
  {
    name: "MCC",
    selector: (row) => formatValueUnit(row.mcc, "%"),
    sortable: true,
    width: "7%",
    right: true,
    sortFunction: (rowA, rowB) => sortFunction(rowA.mcc, rowB.mcc),
  },
  {
    name: "Margen",
    selector: (row) => formatValueUnit(row.margen, "€"),
    sortable: true,
    width: "10%",
    right: true,
    sortFunction: (rowA, rowB) => sortFunction(rowA.margen, rowB.margen),
  },
  {
    name: "MCV",
    selector: (row) => `${row.mcv} %`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.mcv, rowB.mcv),
  },
]);

export const storeDashboardBrandTable = applyAlignment([
  {
    name: "Marca",
    selector: (row) => row.marca.toUpperCase(),
    sortable: true,
    width: "14%",
  },
  {
    name: "Vol. de Ventas",
    selector: (row) => formatValueUnit(row.volVentas, "€"),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.volVentas, rowB.volVentas),
  },
  {
    name: "DTO %",
    selector: (row) => formatValueUnit(row.dto),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.dto, rowB.dto),
  },
  {
    name: "Ventas netas",
    selector: (row) => formatValueUnit(row.ventasNetas, "€"),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.ventasNetas, rowB.ventasNetas),
  },
  {
    name: "Compras netas",
    selector: (row) => formatValueUnit(row.comprasNetas, "€"),
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.comprasNetas, rowB.comprasNetas),
  },
  {
    name: "Uds. Compradas",
    selector: (row) => row.udsCompradas,
    sortable: true,
    right: true,
    width: "12%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.udsCompradas, rowB.udsCompradas),
  },
  {
    name: "Uds. Vendidas",
    selector: (row) => row.udsVendidas,
    sortable: true,
    right: true,
    width: "10%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.udsVendidas, rowB.udsVendidas),
  },
  {
    name: "MCC",
    selector: (row) => formatValueUnit(row.mcc, "%"),
    sortable: true,
    width: "7%",
    right: true,
    sortFunction: (rowA, rowB) => sortFunction(rowA.mcc, rowB.mcc),
  },
  {
    name: "Margen",
    selector: (row) => formatValueUnit(row.margen, "€"),
    sortable: true,
    width: "10%",
    right: true,
    sortFunction: (rowA, rowB) => sortFunction(rowA.margen, rowB.margen),
  },
  {
    name: "MCV",
    selector: (row) => `${row.mcv} %`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.mcv, rowB.mcv),
  },
]);

export const patientDashboardBrandTable = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica.toUpperCase(),
    sortable: true,
    width: "10%",
  },
  {
    name: "Altas",
    selector: (row) => formatValueUnit(row.altas),
    sortable: true,
    right: true,
    width: "5%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.altas, rowB.altas),
  },
  {
    name: "Con\ncompra",
    selector: (row) =>
      formatValueUnit(row.conCompra) +
      ` (${formatValueUnit(row.conCompraP, "%")})`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.conCompra, rowB.conCompra),
  },
  {
    name: "Con\ngraduación",
    selector: (row) =>
      formatValueUnit(row.conGraduacion) +
      ` (${formatValueUnit(row.conGraduacionP, "%")})`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conGraduacion, rowB.conGraduacion),
  },
  {
    name: "Con graduación\ny compra",
    selector: (row) =>
      formatValueUnit(row.conGraduacionConCompra) +
      ` (${formatValueUnit(row.conGraduacionConCompraP, "%")})`,
    sortable: true,
    right: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conGraduacionConCompra, rowB.conGraduacionConCompra),
  },
  {
    name: "Con graduación\ny sin compra",
    selector: (row) =>
      formatValueUnit(row.conGraduacionSinCompra) +
      ` (${formatValueUnit(row.conGraduacionSinCompraP, "%")})`,
    sortable: true,
    right: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conGraduacionSinCompra, rowB.conGraduacionSinCompra),
  },
  {
    name: "Con\naudiometría",
    selector: (row) =>
      formatValueUnit(row.conAudiometria) +
      ` (${formatValueUnit(row.conAudiometriaP, "%")})`,
    sortable: true,
    right: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conAudiometria, rowB.conAudiometria),
  },
  {
    name: "Con audiometría\ny compra",
    selector: (row) =>
      formatValueUnit(row.conAudiometriaConCompra) +
      ` (${formatValueUnit(row.conAudiometriaConCompraP, "%")})`,
    sortable: true,
    width: "8%",
    right: true,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conAudiometriaConCompra, rowB.conAudiometriaConCompra),
  },
  {
    name: "Con audiometría\ny sin compra",
    selector: (row) =>
      formatValueUnit(row.conAudiometriaSinCompra) +
      ` (${formatValueUnit(row.conAudiometriaSinCompraP, "%")})`,
    sortable: true,
    width: "8%",
    right: true,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conAudiometriaSinCompra, rowB.conAudiometriaSinCompra),
  },
  {
    name: "Usuarios\nvista",
    selector: (row) =>
      formatValueUnit(row.usuariosVista) +
      ` (${formatValueUnit(row.usuariosVistaP, "%")})`,
    sortable: true,
    right: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.usuariosVista, rowB.usuariosVista),
  },
  {
    name: "Usuarios\nsol",
    selector: (row) =>
      formatValueUnit(row.usuariosSol) +
      ` (${formatValueUnit(row.usuariosSolP, "%")})`,
    sortable: true,
    right: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.usuariosSol, rowB.usuariosSol),
  },
  {
    name: "Usuarios\nLC",
    selector: (row) =>
      formatValueUnit(row.usuariosLC) +
      ` (${formatValueUnit(row.usuariosLCP, "%")})`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.usuariosLC, rowB.usuariosLC),
  },
  {
    name: "Usuarios\naudífonos",
    selector: (row) =>
      formatValueUnit(row.usuariosAudifonos) +
      ` (${formatValueUnit(row.usuariosAudifonosP, "%")})`,
    sortable: true,
    right: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.usuariosAudifonos, rowB.usuariosAudifonos),
  },
]);

export const existingPatientDashboardBrandTable = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica.toUpperCase(),
    sortable: true,
    width: "10%",
  },
  {
    name: "Recurrente",
    selector: (row) => formatValueUnit(row.recurrentes),
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.recurrentes, rowB.recurrentes),
  },
  {
    name: "Con\ncompra",
    selector: (row) =>
      formatValueUnit(row.conCompra) +
      ` (${formatValueUnit(row.conCompraP, "%")})`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.conCompra, rowB.conCompra),
  },
  {
    name: "Con\ngraduación",
    selector: (row) =>
      formatValueUnit(row.conGraduacion) +
      ` (${formatValueUnit(row.conGraduacionP, "%")})`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conGraduacion, rowB.conGraduacion),
  },
  {
    name: "Con graduación\ny compra",
    selector: (row) =>
      formatValueUnit(row.conGraduacionConCompra) +
      ` (${formatValueUnit(row.conGraduacionConCompraP, "%")})`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conGraduacionConCompra, rowB.conGraduacionConCompra),
  },
  {
    name: "Con graduación\ny sin compra",
    selector: (row) =>
      formatValueUnit(row.conGraduacionSinCompra) +
      ` (${formatValueUnit(row.conGraduacionSinCompraP, "%")})`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conGraduacionSinCompra, rowB.conGraduacionSinCompra),
  },
  {
    name: "Con\naudiometría",
    selector: (row) =>
      formatValueUnit(row.conAudiometria) +
      ` (${formatValueUnit(row.conAudiometriaP, "%")})`,
    sortable: true,
    right: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conAudiometria, rowB.conAudiometria),
  },
  {
    name: "Con audiometría\ny compra",
    selector: (row) =>
      formatValueUnit(row.conAudiometriaConCompra) +
      ` (${formatValueUnit(row.conAudiometriaConCompraP, "%")})`,
    sortable: true,
    width: "8%",
    right: true,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conAudiometriaConCompra, rowB.conAudiometriaConCompra),
  },
  {
    name: "Con audiometría\ny sin compra",
    selector: (row) =>
      formatValueUnit(row.conAudiometriaSinCompra) +
      ` (${formatValueUnit(row.conAudiometriaSinCompraP, "%")})`,
    sortable: true,
    width: "8%",
    right: true,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.conAudiometriaSinCompra, rowB.conAudiometriaSinCompra),
  },
  {
    name: "Usuarios\nvista",
    selector: (row) =>
      formatValueUnit(row.usuariosVista) +
      ` (${formatValueUnit(row.usuariosVistaP, "%")})`,
    sortable: true,
    right: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.usuariosVista, rowB.usuariosVista),
  },
  {
    name: "Usuarios\nsol",
    selector: (row) =>
      formatValueUnit(row.usuariosSol) +
      ` (${formatValueUnit(row.usuariosSolP, "%")})`,
    sortable: true,
    right: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.usuariosSol, rowB.usuariosSol),
  },
  {
    name: "Usuarios\nLC",
    selector: (row) =>
      formatValueUnit(row.usuariosLC) +
      ` (${formatValueUnit(row.usuariosLCP, "%")})`,
    sortable: true,
    right: true,
    width: "7%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.usuariosLC, rowB.usuariosLC),
  },
  {
    name: "Usuarios\naudífonos",
    selector: (row) =>
      formatValueUnit(row.usuariosAudifonos) +
      ` (${formatValueUnit(row.usuariosAudifonosP, "%")})`,
    sortable: true,
    right: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.usuariosAudifonos, rowB.usuariosAudifonos),
  },
]);

export const optometryColumnStore = applyAlignment([
  {
    name: "Fecha",
    selector: (row) =>
      row.fecha ? moment(row.fecha).format("DD/MM/YYYY HH:mm:ss") : "",
    sortable: true,
    width: "160px",
    sortFunction: (a, b) => new Date(a.fecha) - new Date(b.fecha),
  },
  {
    name: "Realizada por",
    selector: (row) => row.realizadaPor,
    sortable: true,
    width: "auto",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.realizadaPor, rowB.realizadaPor),
  },
]);

export const CRMCampaignsColumns = applyAlignment([
  {
    name: "Fecha",
    selector: (row) =>
      row.fecha ? moment(row.fecha).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "10%",
    sortFunction: (a, b) => new Date(a.fecha) - new Date(b.fecha),
  },
  {
    name: "Nombre campaña",
    selector: (row) => row.nombre,
    sortable: true,
    width: "24%",
  },
  {
    name: "Canal",
    selector: (row) => (row.canal || "").toUpperCase(),
    sortable: true,
    width: "7%",
  },
  {
    name: "Lista",
    selector: (row) => row.nombreLista,
    sortable: true,
    width: "15%",
  },
  {
    name: "Contactos",
    selector: (row) => formatValueUnit(row.numContactos),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.numContactos, rowB.numContactos),
  },
  {
    name: "Plantilla",
    selector: (row) => row.nombrePlantilla,
    sortable: true,
    width: "17%",
    style: { overflow: "hidden" },
    cell: (row) => {
      const texto = row.nombrePlantilla || "";
      if (row.canal !== "SMS") return texto;
      const id = String(row.idCampana) + "ttplantilla";
      return (
        <div
          data-tag="allowRowEvents"
          style={{
            position: "relative",
            width: "100%",
            alignSelf: "stretch",
            overflow: "hidden",
          }}
        >
          <div
            data-tag="allowRowEvents"
            data-tooltip-id={id}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              className="crm-plantilla-truncate"
              data-tag="allowRowEvents"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                width: "100%",
              }}
            >
              {texto}
            </span>
          </div>
          <TooltipBox
            id={id}
            content={
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  maxWidth: 350,
                  textAlign: "left",
                }}
              >
                {texto}
              </div>
            }
          ></TooltipBox>
        </div>
      );
    },
  },
  {
    name: "Estado",
    selector: (row) => formatValueUnit(row.pvp, "€"),
    sortable: false,
    style: centertStyle,
    width: "6%",
    cell: (row) =>
      row.estado === "Enviado" ? (
        <>
          <img
            src={`/images/check-blue.svg`}
            data-tooltip-id={String(row.idCampana) + "ttsend"}
            className="icon-pref"
            alt="Enviado"
          ></img>
          <TooltipBox
            id={String(row.idCampana) + "ttsend"}
            content={
              <div className="opt-table-tooltip grid-5">
                <div className="cell combined-cells-5 cell-start-content">
                  Enviado
                </div>
              </div>
            }
          ></TooltipBox>
        </>
      ) : row.estado === "Borrador" ? (
        <>
          <img
            src={`/images/edit-orange.svg`}
            data-tooltip-id={String(row.idCampana) + "ttdraft"}
            className="icon-pref"
            alt="Borrador"
          ></img>
          <TooltipBox
            id={String(row.idCampana) + "ttdraft"}
            content={
              <div className="opt-table-tooltip grid-5">
                <div className="cell combined-cells-5 cell-start-content">
                  Borrador
                </div>
              </div>
            }
          ></TooltipBox>
        </>
      ) : row.estado === "Programado" ? (
        <>
          <img
            src={`/images/calendar-gray.svg`}
            data-tooltip-id={String(row.idCampana) + "ttscheduled"}
            className="icon-pref"
            alt="Programado"
          ></img>
          <TooltipBox
            id={String(row.idCampana) + "ttscheduled"}
            content={
              <div className="opt-table-tooltip grid-5">
                <div className="cell combined-cells-5 cell-start-content">
                  Programado
                </div>
              </div>
            }
          ></TooltipBox>
        </>
      ) : (
        ""
      ),
  },
  {
    name: "Fecha envío",
    selector: (row) =>
      row.fechaEnvio === ""
        ? ""
        : moment(row.fechaEnvio).format("DD/MM/YYYY HH:mm:ss"),
    sortable: true,
    width: "10%",
    sortFunction: (a, b) =>
      (a.fechaEnvio ? new Date(a.fechaEnvio) : new Date(0)) -
      (b.fechaEnvio ? new Date(b.fechaEnvio) : new Date(0)),
  },
  {
    name: "",
    sortable: false,
    width: "3%",
  },
]);

export const OpticalValueColumns = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "75%",
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.total, ""),
    sortable: true,
    width: "25%",
    style: rightStyle,
  },
]);

export const OpticalValueCurrencyColumns = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "75%",
  },
  {
    name: "Total",
    selector: (row) => formatCurrency(row.total),
    sortable: true,
    width: "25%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.total, rowB.total),
  },
]);

const formatterPercentComma = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const OpticalValueRentabilidadColumns = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "75%",
  },
  {
    name: "Total",
    selector: (row) =>
      `${formatterPercentComma.format(parseFloat(row.total) || 0)} %`,
    sortable: true,
    width: "25%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.total, rowB.total),
  },
]);

export const PatientCampaignColumns = (canal) =>
  applyAlignment([
    {
      name: "Código",
      selector: (row) => row.codigoPaciente,
      sortable: true,
      width: "8%",
      sortFunction: (rowA, rowB) =>
        sortFunction(rowA.codigoPaciente, rowB.codigoPaciente),
    },
    {
      name: "Nombre",
      selector: (row) => row.nombre,
      sortable: true,
      width: "15%",
    },
    {
      name: "Apellidos",
      selector: (row) => row.apellidos,
      sortable: true,
      width: "17%",
    },
    {
      name: "Óptica",
      selector: (row) => row.optica,
      sortable: true,
      width: "5%",
      sortFunction: (rowA, rowB) => sortFunction(rowA.optica, rowB.optica),
    },
    {
      name: "Móvil",
      selector: (row) => row.movil,
      sortable: true,
      width: "10%",
    },
    {
      name: "Edad",
      selector: (row) => row.edad,
      sortable: true,
      width: "5%",
      sortFunction: (rowA, rowB) => sortFunction(rowA.edad, rowB.edad),
    },
    {
      name: "Enviado",
      selector: (row) =>
        row.enviado ? (
          <img
            src={`images/support-extra/controlar.svg`}
            className="cellIcon"
            alt="Enviado"
          />
        ) : (
          ""
        ),
      width: "7%",
      style: centertStyle,
    },
    {
      name: "Entregado",
      selector: (row) =>
        row.entregado ? (
          <img
            src={`images/support-extra/controlar.svg`}
            className="cellIcon"
            alt="Entregado"
          />
        ) : (
          ""
        ),
      width: "8%",
      style: centertStyle,
    },
    {
      name: "Abierto",
      selector: (row) =>
        canal !== "SMS" && row.abierto ? (
          <img
            src={`images/support-extra/controlar.svg`}
            className="cellIcon"
            alt="Abierto"
          />
        ) : (
          ""
        ),
      width: "7%",
      style: centertStyle,
    },
    {
      name: "Error",
      selector: (row) =>
        row.error ? (
          <img
            src={`images/support-extra/controlar.svg`}
            className="cellIcon"
            alt="Error"
          />
        ) : (
          ""
        ),
      width: "7%",
      style: centertStyle,
    },
    {
      name: "Realiza compra",
      selector: (row) =>
        row.realizaCompra ? (
          <img
            src={`images/support-extra/controlar.svg`}
            className="cellIcon"
            alt="Realiza compra"
          ></img>
        ) : (
          ""
        ),
      width: "10%",
      style: centertStyle,
    },
  ]);

export const CRMListsColumns = applyAlignment([
  {
    name: "Fecha",
    selector: (row) =>
      row.fecha ? moment(row.fecha).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "10%",
    sortFunction: (a, b) => new Date(a.fecha) - new Date(b.fecha),
  },
  {
    name: "Nombre lista",
    selector: (row) => row.nombre,
    sortable: true,
    width: "21%",
  },
  {
    name: "Canal",
    selector: (row) => (row.canal || "").toUpperCase(),
    sortable: true,
    width: "8%",
  },
  {
    name: "Descripción",
    selector: (row) => row.descripcion,
    sortable: true,
    width: "21%",
  },
  {
    name: "Tipo",
    selector: (row) => row.tipo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Ópticas",
    selector: (row) => row.opticas.join(", "),
    sortable: false,
    width: "19%",
  },
  {
    name: "Contactos",
    selector: (row) => formatValueUnit(row.contactos, ""),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.contactos, rowB.contactos),
  },
  {
    name: "",
    sortable: false,
    width: "3%",
    style: rightStyle,
  },
]);

export const CRMPatientListColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigoPaciente,
    sortable: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.codigoPaciente, rowB.codigoPaciente),
  },
  {
    name: "Nombre",
    selector: (row) => row.nombre,
    sortable: true,
    width: "15%",
  },
  {
    name: "Apellidos",
    selector: (row) => row.apellidos,
    sortable: true,
    width: "20%",
  },
  {
    name: "Grupo",
    selector: (row) => row.grupoOptica,
    sortable: true,
    style: centertStyle,
    width: "9%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.grupoOptica, rowB.grupoOptica),
  },
  {
    name: "Móvil",
    selector: (row) => row.movil,
    sortable: true,
    width: "10%",
  },
  {
    name: "Email",
    selector: (row) => row.email,
    sortable: true,
    width: "15%",
  },
  {
    name: "Edad",
    selector: (row) =>
      row.fechaNacimiento !== ""
        ? moment().diff(moment(row.fechaNacimiento), "years")
        : "",
    sortable: true,
    width: "8%",
    style: centertStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        moment().diff(moment(rowA.fechaNacimiento), "years"),
        moment().diff(moment(rowB.fechaNacimiento), "years"),
      ),
  },
  {
    name: "Últ. compra",
    selector: (row) =>
      row.ultimaCompra === ""
        ? ""
        : moment(row.ultimaCompra).format("DD/MM/YYYY"),
    sortable: true,
    width: "12%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.ultimaCompra, rowB.ultimaCompra),
  },
  {
    name: "",
    sortable: false,
    width: "3%",
    style: rightStyle,
  },
]);

export const ASPatientsBirthday = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigoCliente,
    sortable: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.codigoCliente, rowB.codigoCliente),
  },
  {
    name: "Nombre",
    selector: (row) => row.nombre,
    sortable: true,
    width: "25%",
  },
  {
    name: "Mensaje",
    selector: (row) => row.mensaje,
    sortable: true,
    width: "auto",
  },
  {
    name: "Nacimiento",
    selector: (row) =>
      row.fechaNacimiento
        ? moment(row.fechaNacimiento).format("DD/MM/YYYY")
        : "",
    sortable: true,
    width: "10%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.fechaNacimiento, rowB.fechaNacimiento),
  },
  {
    name: "Edad",
    selector: (row) =>
      row.fechaNacimiento !== ""
        ? moment().diff(moment(row.fechaNacimiento), "years")
        : "",
    sortable: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(
        moment().diff(moment(rowA.fechaNacimiento), "years"),
        moment().diff(moment(rowB.fechaNacimiento), "years"),
      ),
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "8%",
  },
  {
    name: "",
    sortable: false,
    width: "3%",
    style: rightStyle,
  },
]);

export const ASPatientsAppointments = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigoCliente,
    sortable: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.codigoCliente, rowB.codigoCliente),
  },
  {
    name: "Nombre",
    selector: (row) => row.nombre,
    sortable: true,
    width: "20%",
  },
  {
    name: "Mensaje",
    selector: (row) => row.mensaje,
    sortable: true,
    width: "auto",
  },
  {
    name: "Cita",
    selector: (row) =>
      row.fechaCita ? moment(row.fechaCita).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "7%",
    sortFunction: (a, b) => new Date(a.fechaCita) - new Date(b.fechaCita),
  },
  {
    name: "Hora",
    selector: (row) => row.horaCita,
    sortable: true,
    width: "6%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.horaCita, rowB.horaCita),
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "8%",
  },
  {
    name: "",
    sortable: false,
    width: "3%",
    style: rightStyle,
  },
]);

export const ASPatientsCheckUps = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigoCliente,
    sortable: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.codigoCliente, rowB.codigoCliente),
  },
  {
    name: "Nombre",
    selector: (row) => row.nombre,
    sortable: true,
    width: "20%",
  },
  {
    name: "Mensaje",
    selector: (row) => row.mensaje,
    sortable: true,
    width: "auto",
  },
  {
    name: "Revisión",
    selector: (row) =>
      row.fechaRevision ? moment(row.fechaRevision).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "6%",
    sortFunction: (a, b) =>
      new Date(a.fechaRevision) - new Date(b.fechaRevision),
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "8%",
  },
  {
    name: "",
    sortable: false,
    width: "3%",
    style: rightStyle,
  },
]);

export const ASPatientsCommunications = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigoCliente,
    sortable: true,
    width: "8%",
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.codigoCliente, rowB.codigoCliente),
  },
  {
    name: "Nombre",
    selector: (row) => row.nombre,
    sortable: true,
    width: "25%",
  },
  {
    name: "Mensaje",
    selector: (row) => row.mensaje,
    sortable: true,
    width: "auto",
  },
  {
    name: "Venta",
    selector: (row) =>
      row.fechaVenta ? moment(row.fechaVenta).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "6%",
    sortFunction: (a, b) => new Date(a.fechaVenta) - new Date(b.fechaVenta),
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "8%",
  },
  {
    name: "",
    sortable: false,
    width: "3%",
    style: rightStyle,
  },
]);

export const CRMHistoryColumns = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codPaciente,
    sortable: true,
    width: "8%",
  },
  {
    name: "Nombre",
    selector: (row) => row.nombre,
    sortable: true,
    width: "20%",
  },
  {
    name: "Canal",
    selector: (row) => row.canal,
    sortable: true,
    width: "8%",
  },
  {
    name: "Campaña",
    selector: (row) => row.campana,
    sortable: true,
    width: "24%",
  },
  {
    name: "Plantilla / Mensaje",
    selector: (row) => row.mensaje,
    sortable: true,
    width: "32%",
    style: { overflow: "hidden" },
    cell: (row, rowIndex) => {
      const texto = row.mensaje || "";
      if (row.canal !== "SMS") return texto;
      const id = "tthistorialmensaje" + rowIndex;
      return (
        <div
          style={{
            position: "relative",
            width: "100%",
            alignSelf: "stretch",
            overflow: "hidden",
          }}
        >
          <div
            data-tooltip-id={id}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              className="crm-plantilla-truncate"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                width: "100%",
              }}
            >
              {texto}
            </span>
          </div>
          <TooltipBox
            id={id}
            content={
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  maxWidth: 350,
                  textAlign: "left",
                }}
              >
                {texto}
              </div>
            }
          ></TooltipBox>
        </div>
      );
    },
  },
  {
    name: "Envío",
    selector: (row) =>
      row.fechaEnvio ? moment(row.fechaEnvio).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "8%",
    sortFunction: (a, b) => new Date(a.fechaEnvio) - new Date(b.fechaEnvio),
  },
]);

export const invoiceColumns = applyAlignment([
  {
    name: "Fecha",
    selector: (row) =>
      row.fechaFactura ? moment(row.fechaFactura).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "10%",
    sortFunction: (a, b) => new Date(a.fechaFactura) - new Date(b.fechaFactura),
  },
  {
    name: "Proveedor",
    selector: (row) => row.proveedor,
    sortable: true,
    width: "auto",
  },
  {
    name: "Nº factura",
    selector: (row) => row.nFactura,
    sortable: true,
    width: "10%",
  },
  {
    name: "Base",
    selector: (row) => formatValueUnit(row.base, "€"),
    sortable: true,
    width: "8%",
    sortFunction: (rowA, rowB) => sortFunction(rowA.base, rowB.base),
  },
  {
    name: "IVA",
    selector: (row) => formatValueUnit(row.iva, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.iva, rowB.iva),
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.total, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.total, rowB.total),
  },
  {
    name: "Abonado",
    selector: (row) => formatValueUnit(row.abonado, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.abonado, rowB.abonado),
  },
  {
    name: "Pendiente",
    selector: (row) => formatValueUnit(row.pendiente, "€"),
    sortable: true,
    width: "10%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pendiente, rowB.pendiente),
  },
  {
    name: "Pagado",
    selector: (row) =>
      row.pagado ? (
        <img
          src={`/images/check-blue.svg`}
          className="icon-pref"
          alt="Pagado"
        ></img>
      ) : (
        ""
      ),
    sortable: false,
    width: "6%",
  },
]);

export const dueDateColumns = applyAlignment([
  {
    name: "Vencimiento",
    selector: (row) =>
      row.fechaVencimiento
        ? moment(row.fechaVencimiento).format("DD/MM/YYYY")
        : "",
    sortable: true,
    width: "10%",
    sortFunction: (a, b) =>
      new Date(a.fechaVencimiento) - new Date(b.fechaVencimiento),
  },
  {
    name: "Fecha factura",
    selector: (row) =>
      row.fechaFactura ? moment(row.fechaFactura).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "10%",
    sortFunction: (a, b) => new Date(a.fechaFactura) - new Date(b.fechaFactura),
  },
  {
    name: "Proveedor",
    selector: (row) => row.proveedor,
    sortable: true,
    width: "auto",
  },
  {
    name: "Nº factura",
    selector: (row) => row.nFactura,
    sortable: true,
    width: "10%",
  },
  {
    name: "Nº vencimiento",
    selector: (row) => row.nVencimiento,
    sortable: true,
    width: "20%",
  },
  {
    name: "Importe",
    selector: (row) => formatValueUnit(row.importe, "€"),
    sortable: true,
    width: "8%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.importe, rowB.importe),
  },
  {
    name: "Pagado",
    selector: (row) =>
      row.pagado ? (
        <img
          src={`/images/check-blue.svg`}
          className="icon-pref"
          alt="Pagado"
        ></img>
      ) : (
        ""
      ),
    sortable: false,
    width: "6%",
  },
]);

export const invoiceDeliveryNotesColumns = applyAlignment([
  {
    name: "Nº albarán",
    selector: (row) => row.nAlbaran,
    sortable: true,
    width: "auto",
  },
  {
    name: "Base",
    selector: (row) => formatValueUnit(row.base, "€"),
    sortable: true,
    width: "20%",
    style: rightStyle,
  },
  {
    name: "IVA",
    selector: (row) => formatValueUnit(row.iva, "€"),
    sortable: true,
    width: "20%",
    style: rightStyle,
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.total, "€"),
    sortable: true,
    width: "20%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "5%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.idAlbaran}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.idAlbaran)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const invoiceDueDatesColumns = applyAlignment([
  {
    name: "F. vencimiento",
    selector: (row) =>
      row.fechaVencimiento
        ? moment(row.fechaVencimiento).format("DD/MM/YYYY")
        : "",
    sortable: true,
    width: "25%",
    sortFunction: (a, b) =>
      new Date(a.fechaVencimiento) - new Date(b.fechaVencimiento),
  },
  {
    name: "Nº vencimiento",
    selector: (row) => row.nVencimiento,
    sortable: true,
    width: "30%",
  },
  {
    name: "Importe",
    selector: (row) => formatValueUnit(row.importe, "€"),
    sortable: true,
    width: "30%",
  },
  {
    name: "Pagado",
    selector: (row) => {
      <CheckBox active={row.pagado} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "10%",
  },
  {
    name: "",
    sortable: false,
    width: "5%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.idVencimiento}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.idVencimiento)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const invoiceDeliveryNotesSearcherColumns = applyAlignment([
  {
    name: "Fecha",
    selector: (row) =>
      row.fechaAlbaran ? moment(row.fechaAlbaran).format("DD/MM/YYYY") : "",
    sortable: true,
    width: "10%",
    sortFunction: (a, b) => new Date(a.fechaAlbaran) - new Date(b.fechaAlbaran),
  },
  {
    name: "Proveedor",
    selector: (row) => row.proveedor,
    sortable: true,
    width: "auto",
  },
  {
    name: "Nº albarán",
    selector: (row) => row.numeroAlbaran,
    sortable: true,
    width: "20%",
  },
  {
    name: "Base",
    selector: (row) => formatValueUnit(row.pcnTotal, "€"),
    sortable: true,
    width: "10%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pcnTotal, rowB.pcnTotal),
  },
  {
    name: "IVA",
    selector: (row) => formatValueUnit(row.ivaTotal, "€"),
    sortable: true,
    width: "10%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.ivaTotal, rowB.ivaTotal),
  },
  {
    name: "Total",
    selector: (row) => formatValueUnit(row.totalTotal, "€"),
    sortable: true,
    width: "10%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(rowA.totalTotal, rowB.totalTotal),
  },
  {
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    style: centertStyle,
    sortable: false,
    width: "5%",
  },
]);

export const patientNIFColumns = applyAlignment([
  {
    name: "ID",
    selector: (row) => row.documentId,
    sortable: true,
    width: "10%",
  },
  {
    name: "Descripción",
    selector: (row) => row.description,
    sortable: true,
    width: "auto",
  },
  {
    name: "Seleccionado",
    selector: (row) => {
      <CheckBox active={row.active} onClick={() => {}}></CheckBox>;
    },
    style: centertStyle,
    sortable: false,
    width: "20%",
  },
]);

export const dashboardDMColumns = applyAlignment([
  {
    name: "Nombre",
    selector: (row) => row.nombre,
    sortable: true,
    width: "auto",
  },
  {
    name: "Fecha",
    selector: (row) =>
      row.fecha ? moment(row.fecha).format("DD/MM/YYYY HH:mm:ss") : "",
    sortable: true,
    width: "16%",
    sortFunction: (a, b) => new Date(a.fecha) - new Date(b.fecha),
  },
  {
    name: "Tipo",
    selector: (row) => row.tipoArchivo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Tamaño",
    selector: (row) => formatValueUnit(row.tamanoValor, row.tamanoUnidad),
    sortable: false,
    width: "10%",
  },
  {
    name: "Agente",
    selector: (row) => row.agente,
    sortable: true,
    width: "auto",
  },
  {
    name: "",
    selector: () => {},
    style: centertStyle,
    sortable: false,
    width: "12%",
  },
]);

export const fileViewerColumns = applyAlignment([
  {
    name: "Nombre",
    selector: (row) => row.nombreFichero,
    sortable: true,
    width: "auto",
  },
  {
    name: "Fecha",
    selector: (row) =>
      row.fecha ? moment(row.fecha).format("DD/MM/YYYY HH:mm:ss") : "",
    sortFunction: (a, b) => new Date(a.fecha) - new Date(b.fecha),
    sortable: true,
    width: "30%",
  },
  {
    name: "",
    selector: () => {},
    style: centertStyle,
    sortable: false,
    width: "10%",
  },
]);

export const hearingAidsColumnsStoreBulkEdit = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "7%",
  },
  {
    name: "Proveedor",
    selector: (row) => row.nombreProveedor,
    sortable: true,
    width: "9%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "9%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Stock mínimo",
    selector: (row) => formatValueUnit(row.stockMinimo),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Comisión %",
    selector: (row) => row.comision,
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Dto. máx.",
    selector: (row) => row.descuentoMaximo,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "PVP oferta",
    selector: (row) =>
      row.pvpOferta ? formatValueUnit(row.pvpOferta, "€") : "",
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pvpOferta, rowB.pvpOferta),
  },
  {
    name: "Oferta desde",
    selector: (row) =>
      row.ofertaDesde
        ? moment(row.ofertaDesde).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaDesde) - new Date(b.ofertaDesde),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Oferta hasta",
    selector: (row) =>
      row.ofertaHasta
        ? moment(row.ofertaHasta).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaHasta) - new Date(b.ofertaHasta),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Fecha límite",
    selector: (row) =>
      row.fechaLimite
        ? moment(row.fechaLimite).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.fechaLimite) - new Date(b.fechaLimite),
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "4%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const lensesFamilyStoreBulkEdit = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "7%",
  },
  {
    name: "Proveedor",
    selector: (row) => row.nombreProveedor,
    sortable: true,
    width: "9%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "8%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "8%",
  },
  {
    name: "Stock mínimo",
    selector: (row) => formatValueUnit(row.stockMinimo),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Comisión %",
    selector: (row) => row.comision,
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Dto. máx.",
    selector: (row) => row.descuentoMaximo,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "PVP oferta",
    selector: (row) =>
      row.pvpOferta ? formatValueUnit(row.pvpOferta, "€") : "",
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pvpOferta, rowB.pvpOferta),
  },
  {
    name: "Oferta desde",
    selector: (row) =>
      row.ofertaDesde
        ? moment(row.ofertaDesde).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaDesde) - new Date(b.ofertaDesde),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Oferta hasta",
    selector: (row) =>
      row.ofertaHasta
        ? moment(row.ofertaHasta).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaHasta) - new Date(b.ofertaHasta),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Fecha límite",
    selector: (row) =>
      row.fechaLimite
        ? moment(row.fechaLimite).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.fechaLimite) - new Date(b.fechaLimite),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "4%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const contactLensesColumnsStoreBulkEdit = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "7%",
  },
  {
    name: "Proveedor",
    selector: (row) => row.nombreProveedor,
    sortable: true,
    width: "9%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "8%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "8%",
  },
  {
    name: "Stock mínimo",
    selector: (row) => formatValueUnit(row.stockMinimo),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Comisión %",
    selector: (row) => row.comision,
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Dto. máx.",
    selector: (row) => row.descuentoMaximo,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "PVP oferta",
    selector: (row) =>
      row.pvpOferta ? formatValueUnit(row.pvpOferta, "€") : "",
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pvpOferta, rowB.pvpOferta),
  },
  {
    name: "Oferta desde",
    selector: (row) =>
      row.ofertaDesde
        ? moment(row.ofertaDesde).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaDesde) - new Date(b.ofertaDesde),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Oferta hasta",
    selector: (row) =>
      row.ofertaHasta
        ? moment(row.ofertaHasta).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaHasta) - new Date(b.ofertaHasta),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Fecha límite",
    selector: (row) =>
      row.fechaLimite
        ? moment(row.fechaLimite).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.fechaLimite) - new Date(b.fechaLimite),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "4%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const liquidColumnsStoreBulkEdit = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "7%",
  },
  {
    name: "Proveedor",
    selector: (row) => row.nombreProveedor,
    sortable: true,
    width: "9%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "7%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "7%",
  },
  {
    name: "Stock mínimo",
    selector: (row) => formatValueUnit(row.stockMinimo),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Comisión %",
    selector: (row) => row.comision,
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Dto. máx.",
    selector: (row) => row.descuentoMaximo,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "PVP oferta",
    selector: (row) =>
      row.pvpOferta ? formatValueUnit(row.pvpOferta, "€") : "",
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pvpOferta, rowB.pvpOferta),
  },
  {
    name: "Oferta desde",
    selector: (row) =>
      row.ofertaDesde
        ? moment(row.ofertaDesde).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaDesde) - new Date(b.ofertaDesde),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Oferta hasta",
    selector: (row) =>
      row.ofertaHasta
        ? moment(row.ofertaHasta).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaHasta) - new Date(b.ofertaHasta),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Fecha límite",
    selector: (row) =>
      row.fechaLimite
        ? moment(row.fechaLimite).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.fechaLimite) - new Date(b.fechaLimite),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "4%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const frameColumnsStoreBulkEdit = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "7%",
  },
  {
    name: "Proveedor",
    selector: (row) => row.nombreProveedor,
    sortable: true,
    width: "9%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "8%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Stock mínimo",
    selector: (row) => formatValueUnit(row.stockMinimo),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Comisión %",
    selector: (row) => row.comision,
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Dto. máx.",
    selector: (row) => row.descuentoMaximo,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "PVP oferta",
    selector: (row) =>
      row.pvpOferta ? formatValueUnit(row.pvpOferta, "€") : "",
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pvpOferta, rowB.pvpOferta),
  },
  {
    name: "Oferta desde",
    selector: (row) =>
      row.ofertaDesde
        ? moment(row.ofertaDesde).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaDesde) - new Date(b.ofertaDesde),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Oferta hasta",
    selector: (row) =>
      row.ofertaHasta
        ? moment(row.ofertaHasta).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaHasta) - new Date(b.ofertaHasta),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Fecha límite",
    selector: (row) =>
      row.fechaLimite
        ? moment(row.fechaLimite).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.fechaLimite) - new Date(b.fechaLimite),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "4%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const variousColumnsStoreBulkEdit = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "7%",
  },
  {
    name: "Proveedor",
    selector: (row) => row.nombreProveedor,
    sortable: true,
    width: "9%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "8%",
  },
  {
    name: "Producto",
    selector: (row) => row.producto,
    sortable: true,
    width: "8%",
  },
  {
    name: "Stock mínimo",
    selector: (row) => formatValueUnit(row.stockMinimo),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Comisión %",
    selector: (row) => row.comision,
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Dto. máx.",
    selector: (row) => row.descuentoMaximo,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "PVP oferta",
    selector: (row) =>
      row.pvpOferta ? formatValueUnit(row.pvpOferta, "€") : "",
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pvpOferta, rowB.pvpOferta),
  },
  {
    name: "Oferta desde",
    selector: (row) =>
      row.ofertaDesde
        ? moment(row.ofertaDesde).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaDesde) - new Date(b.ofertaDesde),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Oferta hasta",
    selector: (row) =>
      row.ofertaHasta
        ? moment(row.ofertaHasta).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaHasta) - new Date(b.ofertaHasta),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Fecha límite",
    selector: (row) =>
      row.fechaLimite
        ? moment(row.fechaLimite).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.fechaLimite) - new Date(b.fechaLimite),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "4%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const sunglassesColumnsStoreBulkEdit = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "7%",
  },
  {
    name: "Proveedor",
    selector: (row) => row.nombreProveedor,
    sortable: true,
    width: "9%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "8%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Stock mínimo",
    selector: (row) => formatValueUnit(row.stockMinimo),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Comisión %",
    selector: (row) => row.comision,
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Dto. máx.",
    selector: (row) => row.descuentoMaximo,
    sortable: true,
    width: "6%",
    style: rightStyle,
  },
  {
    name: "PVP",
    selector: (row) => formatValueUnit(row.PVP, "€"),
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.PVP, rowB.PVP),
  },
  {
    name: "PVP oferta",
    selector: (row) =>
      row.pvpOferta ? formatValueUnit(row.pvpOferta, "€") : "",
    sortable: true,
    width: "7%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.pvpOferta, rowB.pvpOferta),
  },
  {
    name: "Oferta desde",
    selector: (row) =>
      row.ofertaDesde
        ? moment(row.ofertaDesde).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaDesde) - new Date(b.ofertaDesde),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Oferta hasta",
    selector: (row) =>
      row.ofertaHasta
        ? moment(row.ofertaHasta).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.ofertaHasta) - new Date(b.ofertaHasta),
    sortable: true,
    width: "9%",
    style: rightStyle,
  },
  {
    name: "Fecha límite",
    selector: (row) =>
      row.fechaLimite
        ? moment(row.fechaLimite).format("DD/MM/YYYY HH:mm:ss")
        : "",
    sortFunction: (a, b) => new Date(a.fechaLimite) - new Date(b.fechaLimite),
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Stock",
    selector: (row) => (row.sinControlStock ? "-" : formatValueUnit(row.stock)),
    sortable: true,
    width: "4%",
    style: rightStyle,
    sortFunction: (rowA, rowB) =>
      sortFunction(
        rowA.sinControlStock ? "-" : rowA.stock,
        rowB.sinControlStock ? "-" : rowB.stock,
      ),
  },
  {
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const hearingAidsColumnsOrders = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód.",
    selector: (row) => row.codigo,
    sortable: true,
    width: "7%",
  },
  {
    name: "Cód. cli.",
    id: "patientCode",
    selector: (row) => row.codigoPaciente,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "12%",
  },
  {
    name: "Tipo",
    selector: (row) => row.tipo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Entre dB",
    selector: (row) => row.entreDB,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Hasta dB",
    selector: (row) => row.hastaDB,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Pedido",
    selector: (row) =>
      row.fechaPedido ? moment(row.fechaPedido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) => new Date(a.fechaPedido) - new Date(b.fechaPedido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Recibido",
    selector: (row) =>
      row.fechaRecibido ? moment(row.fechaRecibido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) =>
      new Date(a.fechaRecibido) - new Date(b.fechaRecibido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "8%",
  },
  {
    name: "Uds",
    selector: (row) => formatValueUnit(row.uds),
    sortable: true,
    width: "5%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.uds, rowB.uds),
  },
  {
    name: "Auto",
    selector: (row) =>
      row.auto ? (
        <>
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Automatizado"
          ></img>
        </>
      ) : (
        ""
      ),
    sortable: true,
    width: "5%",
  },
  {
    name: "ID Pedido",
    selector: (row) => row.idPedido,
    sortable: true,
    width: "8%",
  },
  {
    id: "check",
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const lensesColumnsOrders = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód.",
    selector: (row) => row.codigo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód. cli.",
    id: "patientCode",
    selector: (row) => row.codigoPaciente,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "14%",
  },
  {
    name: "Índ",
    selector: (row) => row.ind,
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Ojo",
    selector: (row) => row.ojo,
    sortable: true,
    width: "5%",
  },
  {
    name: "Pedido",
    selector: (row) =>
      row.fechaPedido ? moment(row.fechaPedido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) => new Date(a.fechaPedido) - new Date(b.fechaPedido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Recibido",
    selector: (row) =>
      row.fechaRecibido ? moment(row.fechaRecibido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) =>
      new Date(a.fechaRecibido) - new Date(b.fechaRecibido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "6%",
  },
  {
    name: "Uds",
    selector: (row) => formatValueUnit(row.uds),
    sortable: true,
    width: "5%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.uds, rowB.uds),
  },
  {
    name: "Auto",
    selector: (row) =>
      row.auto ? (
        <>
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Automatizado"
          ></img>
        </>
      ) : (
        ""
      ),
    sortable: true,
    width: "5%",
  },
  {
    name: "ID Pedido",
    selector: (row) => row.idPedido,
    sortable: true,
    width: "8%",
  },
  {
    id: "check",
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const contactLensesColumnsOrders = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód.",
    selector: (row) => row.codigo,
    sortable: true,
    width: "6%",
  },
  {
    name: "Cód. cli.",
    id: "patientCode",
    selector: (row) => row.codigoPaciente,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "13%",
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: true,
    width: "4%",
  },
  {
    name: "Rad 1",
    selector: (row) => row.rad1,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Rad 2",
    selector: (row) => row.rad2,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Diá",
    selector: (row) => row.dia,
    sortable: true,
    width: "4%",
    style: rightStyle,
  },
  {
    name: "Pedido",
    selector: (row) =>
      row.fechaPedido ? moment(row.fechaPedido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) => new Date(a.fechaPedido) - new Date(b.fechaPedido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Recibido",
    selector: (row) =>
      row.fechaRecibido ? moment(row.fechaRecibido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) =>
      new Date(a.fechaRecibido) - new Date(b.fechaRecibido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "6%",
  },
  {
    name: "Uds",
    selector: (row) => formatValueUnit(row.uds),
    sortable: true,
    width: "4%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.uds, rowB.uds),
  },
  {
    name: "Auto",
    selector: (row) =>
      row.auto ? (
        <>
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Automatizado"
          ></img>
        </>
      ) : (
        ""
      ),
    sortable: true,
    width: "5%",
  },
  {
    name: "ID Pedido",
    selector: (row) => row.idPedido,
    sortable: true,
    width: "7%",
  },
  {
    id: "check",
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const liquidColumnsOrders = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód.",
    selector: (row) => row.codigo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód. cli.",
    id: "patientCode",
    selector: (row) => row.codigoPaciente,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "22%",
  },
  {
    name: "Capacidad (ml)",
    selector: (row) => formatValueUnit(row.capacidad),
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "Pedido",
    selector: (row) =>
      row.fechaPedido ? moment(row.fechaPedido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) => new Date(a.fechaPedido) - new Date(b.fechaPedido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Recibido",
    selector: (row) =>
      row.fechaRecibido ? moment(row.fechaRecibido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) =>
      new Date(a.fechaRecibido) - new Date(b.fechaRecibido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "7%",
  },
  {
    name: "Uds",
    selector: (row) => formatValueUnit(row.uds),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.uds, rowB.uds),
  },
  {
    name: "Auto",
    selector: (row) =>
      row.auto ? (
        <>
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Automatizado"
          ></img>
        </>
      ) : (
        ""
      ),
    sortable: true,
    width: "6%",
  },
  {
    name: "ID Pedido",
    selector: (row) => row.idPedido,
    sortable: true,
    width: "8%",
  },
  {
    id: "check",
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const frameColumnsOrders = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód.",
    selector: (row) => row.codigo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód. cli.",
    id: "patientCode",
    selector: (row) => row.codigoPaciente,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "10%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "11%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: true,
    width: "10%",
  },
  {
    name: "Pedido",
    selector: (row) =>
      row.fechaPedido ? moment(row.fechaPedido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) => new Date(a.fechaPedido) - new Date(b.fechaPedido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Recibido",
    selector: (row) =>
      row.fechaRecibido ? moment(row.fechaRecibido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) =>
      new Date(a.fechaRecibido) - new Date(b.fechaRecibido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "8%",
  },
  {
    name: "Uds",
    selector: (row) => formatValueUnit(row.uds),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.uds, rowB.uds),
  },
  {
    name: "Auto",
    selector: (row) =>
      row.auto ? (
        <>
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Automatizado"
          ></img>
        </>
      ) : (
        ""
      ),
    sortable: true,
    width: "6%",
  },
  {
    name: "ID Pedido",
    selector: (row) => row.idPedido,
    sortable: true,
    width: "8%",
  },
  {
    id: "check",
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const variousColumnsOrders = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód.",
    selector: (row) => row.codigo,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód. cli.",
    id: "patientCode",
    selector: (row) => row.codigoPaciente,
    sortable: true,
    width: "8%",
    style: rightStyle,
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "12%",
  },
  {
    name: "Producto",
    selector: (row) => row.producto,
    sortable: true,
    width: "18%",
  },
  {
    name: "Pedido",
    selector: (row) =>
      row.fechaPedido ? moment(row.fechaPedido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) => new Date(a.fechaPedido) - new Date(b.fechaPedido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Recibido",
    selector: (row) =>
      row.fechaRecibido ? moment(row.fechaRecibido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) =>
      new Date(a.fechaRecibido) - new Date(b.fechaRecibido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "8%",
  },
  {
    name: "Uds",
    selector: (row) => formatValueUnit(row.uds),
    sortable: true,
    width: "6%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.uds, rowB.uds),
  },
  {
    name: "Auto",
    selector: (row) =>
      row.auto ? (
        <>
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Automatizado"
          ></img>
        </>
      ) : (
        ""
      ),
    sortable: true,
    width: "6%",
  },
  {
    name: "ID Pedido",
    selector: (row) => row.idPedido,
    sortable: true,
    width: "8%",
  },
  {
    id: "check",
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const sunglassesColumnsOrders = applyAlignment([
  {
    name: "Óptica",
    selector: (row) => row.nombreOptica,
    sortable: true,
    width: "8%",
  },
  {
    name: "Cód.",
    selector: (row) => row.codigo,
    sortable: true,
    width: "6%",
  },
  {
    name: "Cód. cli.",
    id: "patientCode",
    selector: (row) => row.codigoPaciente,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "12%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "13%",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: true,
    width: "10%",
  },
  {
    name: "Pedido",
    selector: (row) =>
      row.fechaPedido ? moment(row.fechaPedido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) => new Date(a.fechaPedido) - new Date(b.fechaPedido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Recibido",
    selector: (row) =>
      row.fechaRecibido ? moment(row.fechaRecibido).format("DD/MM/YYYY") : "",
    sortFunction: (a, b) =>
      new Date(a.fechaRecibido) - new Date(b.fechaRecibido),
    sortable: true,
    width: "8%",
  },
  {
    name: "Estado",
    selector: (row) => row.estado,
    sortable: true,
    width: "8%",
  },
  {
    name: "Uds",
    selector: (row) => formatValueUnit(row.uds),
    sortable: true,
    width: "5%",
    style: rightStyle,
    sortFunction: (rowA, rowB) => sortFunction(rowA.uds, rowB.uds),
  },
  {
    name: "Auto",
    selector: (row) =>
      row.auto ? (
        <>
          <img
            src={`/images/check-orange.svg`}
            className="icon-pref"
            alt="Automatizado"
          ></img>
        </>
      ) : (
        ""
      ),
    sortable: true,
    width: "5%",
  },
  {
    name: "ID Pedido",
    selector: (row) => row.idPedido,
    sortable: true,
    width: "8%",
  },
  {
    id: "check",
    name: "",
    selector: (row) => {
      <CheckBox active={""} onClick={() => {}}></CheckBox>;
    },
    sortable: false,
    width: "2%",
  },
]);

export const hearingAidsColumnsTransfer = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "15%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "auto",
  },
  {
    name: "Tipo",
    selector: (row) => row.tipo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Entre dB",
    selector: (row) => row.entreDB,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "Hasta dB",
    selector: (row) => row.hastaDB,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "Stock origen",
    selector: (row) => row.stockOrigen,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    id: "stock-transfer",
    name: "Stock traspaso",
    selector: (row) => row.stockTraspaso,
    sortable: true,
    width: "12%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    style: rightStyle,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.id}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.id)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const lensesColumnsTransfer = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "12%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "auto",
  },
  {
    name: "Índ",
    selector: (row) => row.ind,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: true,
    width: "7%",
    style: rightStyle,
  },
  {
    name: "Stock origen",
    selector: (row) => row.stockOrigen,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    id: "stock-transfer",
    name: "Stock traspaso",
    selector: (row) => row.stockTraspaso,
    sortable: true,
    width: "12%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    style: rightStyle,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.id}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.id)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const contactLensesColumnsTransfer = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "auto",
  },
  {
    name: "Esf",
    selector: (row) =>
      row.esf !== "" && row.esf != null ? parseFloat(row.esf).toFixed(2) : "",
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Cil",
    selector: (row) =>
      row.cil !== "" && row.cil != null ? parseFloat(row.cil).toFixed(2) : "",
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Eje",
    selector: (row) => row.eje,
    sortable: true,
    width: "5%",
  },
  {
    name: "Rad 1",
    selector: (row) => row.rad1,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Rad 2",
    selector: (row) => row.rad2,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Diá",
    selector: (row) => row.dia,
    sortable: true,
    width: "5%",
    style: rightStyle,
  },
  {
    name: "Stock origen",
    selector: (row) => row.stockOrigen,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    id: "stock-transfer",
    name: "Stock traspaso",
    selector: (row) => row.stockTraspaso,
    sortable: true,
    width: "12%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    style: rightStyle,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.id}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.id)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const liquidColumnsTransfer = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Nombre comercial",
    selector: (row) => row.nombreComercial,
    sortable: true,
    width: "auto",
  },
  {
    name: "Capacidad (ml)",
    selector: (row) => formatValueUnit(row.capacidad),
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    name: "Stock origen",
    selector: (row) => row.stockOrigen,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    id: "stock-transfer",
    name: "Stock traspaso",
    selector: (row) => row.stockTraspaso,
    sortable: true,
    width: "12%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    style: rightStyle,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.id}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.id)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const frameColumnsTransfer = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "15%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "auto",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: true,
    width: "15%",
  },
  {
    name: "Stock origen",
    selector: (row) => row.stockOrigen,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    id: "stock-transfer",
    name: "Stock traspaso",
    selector: (row) => row.stockTraspaso,
    sortable: true,
    width: "12%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    style: rightStyle,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.id}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.id)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const variousColumnsTransfer = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "15%",
  },
  {
    name: "Producto",
    selector: (row) => row.producto,
    sortable: true,
    width: "auto",
  },
  {
    name: "Stock origen",
    selector: (row) => row.stockOrigen,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    id: "stock-transfer",
    name: "Stock traspaso",
    selector: (row) => row.stockTraspaso,
    sortable: true,
    width: "12%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    style: rightStyle,
    width: "2%",
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.id}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.id)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const sunglassesColumnsTransfer = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Marca",
    selector: (row) => row.marca,
    sortable: true,
    width: "15%",
  },
  {
    name: "Modelo",
    selector: (row) => row.modelo,
    sortable: true,
    width: "auto",
  },
  {
    name: "Color",
    selector: (row) => row.color,
    sortable: true,
    width: "15%",
  },
  {
    name: "Stock origen",
    selector: (row) => row.stockOrigen,
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
  {
    id: "stock-transfer",
    name: "Stock traspaso",
    selector: (row) => row.stockTraspaso,
    sortable: true,
    width: "12%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "2%",
    style: rightStyle,
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.id}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.id)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const unavailableProductColumnsTransfer = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "10%",
  },
  {
    name: "Descripción",
    selector: (row) => (row.id === -1 ? "N/A" : ""),
    sortable: true,
    width: "auto",
  },
  {
    name: "Stock origen",
    selector: (row) => row.stockOrigen,
    sortable: true,
    width: "15%",
    style: rightStyle,
  },
  {
    id: "stock-transfer",
    name: "Stock traspaso",
    selector: (row) => row.stockTraspaso,
    sortable: true,
    width: "15%",
    style: rightStyle,
  },
  {
    name: "",
    sortable: false,
    width: "2%",
    style: rightStyle,
    cell: (row) => (
      <>
        <img
          src={`/images/remove-blue.svg`}
          data-tooltip-id={row.codigo}
          className="icon-pref"
          alt="Eliminar"
        ></img>
        <TooltipBox
          id={String(row.codigo)}
          content={
            <div className="opt-table-tooltip grid-5">
              <div className="cell combined-cells-5 cell-start-content">
                Borrar
              </div>
            </div>
          }
        ></TooltipBox>
      </>
    ),
  },
]);

export const columnsInventory = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "15%",
  },
  {
    name: "Descripción",
    selector: (row) => row.descripcion,
    sortable: true,
    width: "auto",
  },
  {
    name: "Stock inventario",
    selector: (row) => row.stockInventario,
    sortable: true,
    width: "15%",
    style: rightStyle,
  },
]);

export const columnsInventoryCheck = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "15%",
  },
  {
    name: "Descripción",
    selector: (row) => row.descripcion,
    sortable: true,
    width: "auto",
  },
  {
    name: "Stock inventario",
    id: "inventory-stock",
    selector: (row) => row.stockInventario,
    sortable: true,
    width: "15%",
    style: rightStyle,
  },
  {
    name: "Stock OPTIWIN",
    selector: (row) => row.stockOPTIWIN,
    sortable: true,
    width: "15%",
    style: rightStyle,
  },
  {
    name: "Ajuste",
    selector: (row) => (row.stockInventario ?? 0) - (row.stockOPTIWIN ?? 0),
    format: (row) => {
      const diff = (row.stockInventario ?? 0) - (row.stockOPTIWIN ?? 0);
      return diff > 0 ? `+${diff}` : `${diff}`;
    },
    sortable: true,
    width: "10%",
    style: rightStyle,
  },
]);

export const columnsInventoryNotFound = applyAlignment([
  {
    name: "Código",
    selector: (row) => row.codigo,
    sortable: true,
    width: "15%",
  },
  {
    name: "Descripción",
    selector: (row) => row.descripcion,
    sortable: true,
    width: "auto",
  },
  {
    name: "Stock inventario",
    selector: (row) => row.stockInventario,
    sortable: true,
    width: "15%",
    style: rightStyle,
  },
  {
    name: "Stock OPTIWIN",
    selector: (row) =>
      row.stockOPTIWIN === "" ? "NO EXISTE" : row.stockOPTIWIN,
    sortable: true,
    width: "15%",
    style: centertStyle,
  },
]);
